import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useConfig } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useAuth } from '../context/AuthContext';
import { canSeeRiderConfig } from '../lib/permissions';
import { useMusicians } from '../hooks/useMusicians';
import { useCrew } from '../hooks/useCrew';
import { useProfileContext } from '../hooks/useProfileContext';
import { listStagePlots, createStagePlot, type StagePlotListItem } from '../api/stageplots';
import { apiGetBand, apiGetTeam, apiGetPdfLayoutEffective } from '../api';
import { stagePlotToSvg, inlineStagePlotImages } from '../components/StagePlotEditor/StagePlotCanvas';
import StagePlotThumb from '../components/StagePlotEditor/StagePlotThumb';
import { riserItems, type RiserConfig } from '../components/StagePlotEditor/riserConfig';
import type { StagePlot } from '../shared/types/stageplot';
import { createEmptyStagePlot } from '../shared/types/stageplot';
import TransportToggle from '../components/TransportToggle';
import { useNavigate } from 'react-router-dom';
// pdfmake shared helper
import { generatePdfBlobViaServer } from '../lib/pdf';
import { STAND_LABELS, isRealMicStand, createDefaultAppConfig, LIGHT_FIXTURE_CATEGORIES, mergeStageRacks, type ProvidedBy, type Musician, type CrewMember, type AppConfig, type TransportType, type SidefillConfig, type StageRack, type LightPlotRender } from '../types';
import { computeRackUsage, rackCapacities } from '../lib/rackUsage';
import { excludedFromBackline } from '../lib/backlineFilter';
import { resolveLightPlotsForPdf, sharedLightPlots } from '../lib/lightPlots';
import type { TDocumentDefinitions, Content } from 'pdfmake/interfaces';
import { createDefaultPdfLayout, mergePdfLayouts, fieldStyleOverride, DEFAULT_PDF_LAYOUT_TEXTS, type PdfLayout, type PdfLayoutField, type PdfLayoutStyle, type PdfSectionId } from '../lib/riderLayout';
import { t } from '../lib/pdfI18n';
import { countIemUnits, selectedTransmitterPeople, selectedWiredPeople } from '../lib/iemUnits';
import { resolveBacklineProvider, backlineOverrideFor } from '../lib/backlineProvider';
import { cleanLines } from '../lib/multiline';
import { compareMusicians, compareCrew, sortMusicians, sortCrew } from '../lib/peopleSort';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import PdfPreview from '../components/PdfPreview';
import ImportRiderAIModal from '../components/ImportRiderAIModal';
import PageHeader from '../components/PageHeader';
import RiderReadOnlyView from '../components/RiderReadOnlyView';

// Rasterize an SVG string to a PNG data URL so server-side pdfmake (which
// cannot render raw SVG) can embed the stage plot. Returns null on failure or
// timeout — callers must fall back to a text placeholder.
function svgToPngDataUrl(svg: string, width = 1000): Promise<string | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 2500);
    try {
      const img = new Image();
      const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      img.onload = () => {
        clearTimeout(timer);
        const scale = width / (img.width || width);
        const h = Math.round((img.height || width) * scale);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, width, h);
        try {
          resolve(canvas.toDataURL('image/png'));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => { clearTimeout(timer); resolve(null); };
      img.src = url;
    } catch {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

// ---------------------------------------------------------------------------
// Input / Output List COLUMN definitions (2026-08-17). Each column maps to a
// stable field id — the SAME id used as a section-field id in the layout
// editor, so the user can show / hide / reorder the rider tables' columns with
// the existing field mechanism. `get` extracts the cell value for a row;
// `width` mirrors the original hard-coded pdfmake column widths.
// ---------------------------------------------------------------------------
interface ListColumn<T> {
  id: string; // stable id, e.g. 'input-list.ch'
  label: string; // header label, e.g. 'Ch'
  width: number | string; // pdfmake column width
  center?: boolean; // center-align the header + cells
  get: (t: T) => string;
}

interface InputTrackRow {
  ch: number; name: string; rack: string; mic: string; conn: string;
  tx: string; stand: string; provided: string; notes: string;
}
interface OutputTrackRow {
  rack: string; ch: number; name: string; type: string; notes: string;
}

// Une note qui se contente de dire « provided by the band/venue » duplique la
// colonne PROV. : on ne l'imprime pas (demande user 2026-09-19). La DONNÉE de la
// carte n'est jamais modifiée — c'est un simple filtre d'affichage.
export const isProviderOnlyNote = (note: string | undefined): boolean => {
  const n = (note || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!n) return false;
  const provider = /\b(band|groupe|grupo|venue|salle|house|sono)\b/;
  if (/^(provided by|fourni par|proporcionado por)\b/.test(n) && provider.test(n)) return true;
  return /^(the |le |la )?(band|groupe|venue|salle|grupo)$/.test(n);
};

export const INPUT_LIST_COLUMNS: ListColumn<InputTrackRow>[] = [
  // ⚠️ OPTION D (2026-10-07) — l'Input List 9 colonnes tient désormais dans les
  // 515,3 pt utiles grâce au layout DENSE `bandzTableDense` (padding 8→3 pt par
  // côté, cf. TABLE_LAYOUT / INPUT_LIST_TABLE_LAYOUT commentaire) ET à ces
  // largeurs FIXES mesurées au rendu réel (Roboto 10 gras, en-tête inclus) :
  // Ch 22 · Rack 36 · Mic 36 · Connection 71 · Transmitter 76 · Stand 46 ·
  // Provided by 59 ⇒ Σ = 346 pt. Budget avec p=3 : 515,28 − 2·3·(9−1) − 2·55
  // = 357 pt (My Band) ⇒ marge ≥ 11 pt. AUCUN libellé ne change (décision user).
  // Preuve : scripts/qa-rider-overflow-words.ts (0 mot hors marge sur les 4
  // groupes × transports) + src/__tests__/inputListBudget.test.ts (garde-fou).
  { id: 'input-list.ch', label: 'Ch', width: 22, center: true, get: (t) => (t.ch > 0 ? String(t.ch) : '') },
  { id: 'input-list.track', label: 'Track', width: '*', get: (t) => t.name },
  { id: 'input-list.rack', label: 'Rack', width: 36, get: (t) => t.rack },
  { id: 'input-list.mic', label: 'Mic', width: 36, get: (t) => t.mic },
  { id: 'input-list.connection', label: 'Connection', width: 71, get: (t) => t.conn },
  { id: 'input-list.transmitter', label: 'Transmitter', width: 76, get: (t) => t.tx },
  { id: 'input-list.stand', label: 'Stand', width: 46, get: (t) => t.stand },
  // « Provided by » : largeur NUMÉRIQUE (59) et non 'auto' — sur une table
  // surabondante pdfmake RÉTRÉCIT les 'auto' et coupe l'en-tête. 59 pt =
  // « PROVIDED » (8 car.) + marge de cellule ; l'en-tête peut se replier sur
  // « PROVIDED / BY » sur deux lignes (le libellé reste identique).
  { id: 'input-list.provided', label: 'Provided by', width: 59, get: (t) => t.provided },
  { id: 'input-list.notes', label: 'Notes', width: '*', get: (t) => t.notes },
];

export const OUTPUT_LIST_COLUMNS: ListColumn<OutputTrackRow>[] = [
  { id: 'output-list.rack', label: 'Rack', width: 90, get: (t) => t.rack },
  { id: 'output-list.ch', label: 'Ch', width: 34, center: true, get: (t) => (t.ch > 0 ? String(t.ch) : '') },
  { id: 'output-list.track', label: 'Track', width: '*', get: (t) => t.name },
  { id: 'output-list.type', label: 'Type', width: 80, get: (t) => t.type },
  { id: 'output-list.notes', label: 'Notes', width: '*', get: (t) => t.notes },
];

// Aggregated backline row (one per merged gear item). `hasLabel` records whether
// the SOURCE item carried a non-empty label: when it did NOT, the Item cell
// shows the type (label || type) so printing the type again in the Type column
// would duplicate it — the Type cell is then left EMPTY (v0.641).
interface BacklineRow {
  qty: number;
  type: string; // raw type of the source item (may be a slug or free text)
  label: string; // display item name = label || type || 'Item'

  model: string;
  providedBy: ProvidedBy;
  notes: string;
  imageUrl?: string;
}

// Humanise a backline type for the Type column. Machine-like slugs (no space,
// all-lowercase: 'keyboard_stand', 'guitar-stand', 'box') get their separators
// turned into spaces and their first letter capitalised → 'Keyboard stand'.
// Human-typed free text ('Snare drum', 'Cymbal stand', 'Splash') is left EXACTLY
// as typed — capitalising it would alter the user's own wording.
const humanizeBacklineType = (raw: string): string => {
  const s = (raw || '').trim();
  if (!s) return '';
  const isSlug = !/\s/.test(s) && s === s.toLowerCase();
  if (!isSlug) return s;
  const spaced = s.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

// Backline COLUMNS (v0.641, 2026-10-07) — show/hide/reorder from the Rider
// Layout editor exactly like Input/Output List. Widths: Qty / Provided by are
// 'auto' (v0.644 — short+bounded content), the text columns '*'. The reference
// IMAGE stays inside the Notes cell (built by hand in the builder, since it is a
// compound node the plain `get` cannot express).
export const BACKLINE_COLUMNS: ListColumn<BacklineRow>[] = [
  // 'auto' (v0.644) : Qté = petits entiers, Provided by = Band/Venue (contenus
  // COURTS ET BORNÉS) → largeur naturelle, jamais coupée. Les colonnes à texte
  // libre (Type/Item/Model/Notes) restent '*' — 'auto' n'a aucun plafond.
  { id: 'backline.qty', label: 'Qty', width: 'auto', center: true, get: (r) => String(r.qty) },
  // The Type column is ALWAYS filled (user 2026-10-15: « il manque des champs dans le
  // pdf dans la liste du backline (type) » — on the printed rider, 4 of 13 rows had an
  // EMPTY Type cell, which reads as a broken/incomplete table). The old rule blanked
  // the cell for items with no label of their own ("the Description already falls back
  // to the type, do not print it twice") — but it made the column look randomly empty,
  // it lost the type entirely when the Description column was hidden, and it pushed the
  // user to re-type the type into the Description by hand, which recreates the very
  // duplication it tried to avoid. Description keeps its fallback (`label || type`), so
  // no cell is ever blank.
  { id: 'backline.type', label: 'Type', width: '*', get: (r) => humanizeBacklineType(r.type) },
  { id: 'backline.item', label: 'Description', width: '*', get: (r) => r.label },
  { id: 'backline.model', label: 'Model', width: '*', get: (r) => r.model || '' },
  { id: 'backline.provided', label: 'Provided by', width: 'auto', center: true, get: (r) => (r.providedBy === 'venue' ? t('pdf.misc.venue') : t('pdf.misc.band')) },
  { id: 'backline.notes', label: 'Notes', width: '*', get: (r) => r.notes },
];

// Mic Stands row: one aggregated line per (stand type + boom + provider).
// Filtered upstream by `isRealMicStand` (a `clip` never produces a row — v0.647)
// and on the stands provided by the VENUE (the band brings its own — the table
// states the venue requirement explicitly, unchanged since v0.598).
interface MicStandRow {
  qty: number;
  label: string; // stand type label, e.g. 'Small'
  withoutBoom: boolean;
  providedBy: ProvidedBy;
  /** WHO the stand is for — the destination. Derived from the track carriers
   *  (musician/crew names, in musician order) unless a track carries an explicit
   *  `destination` override. (v0.651) */
  destination: string;
}

// Mic Stands COLUMNS (v0.648, 2026-10-07) — show/hide/reorder/resize from the
// Rider Layout editor exactly like Input/Output List and Backline. Widths mirror
// the historical `['auto','*','auto','auto']` (v0.644): Qty / Boom / Provided by
// are short + bounded → 'auto' (never clipped), Type is '*'.
export const MIC_STANDS_COLUMNS: ListColumn<MicStandRow>[] = [
  { id: 'mic-stands.qty', label: 'Qty', width: 'auto', center: true, get: (r) => String(r.qty) },
  { id: 'mic-stands.type', label: 'Type', width: '*', get: (r) => r.label },
  { id: 'mic-stands.boom', label: 'Boom', width: 'auto', center: true, get: (r) => (r.withoutBoom ? t('pdf.col.without') : t('pdf.col.with')) },
  // Destination (v0.651) — WHO/where the stand is for. Bounded NUMERIC width
  // (72): the minimum that shows the full "DESTINATION" header on one line and
  // keeps the widest real no-'*' table (SAMAEL) inside the usable width. A
  // numeric width WRAPS long values rather than clipping them. Value derived +
  // overridable per track.
  { id: 'mic-stands.destination', label: 'Destination', width: 72, get: (r) => r.destination },
  { id: 'mic-stands.provided', label: 'Provided by', width: 'auto', center: true, get: (r) => (r.providedBy === 'venue' ? t('pdf.misc.venue') : t('pdf.misc.band')) },
];

// Lights fixtures row: one fixture item of a lighting category.
interface FixtureRow {
  qty: number;
  model: string;
  description: string;
  notes: string;
}

// Lights FIXTURES COLUMNS (v0.648, 2026-10-07) — the 4 columns of the fixtures
// tables rendered INSIDE the LIGHTS section (one table per lighting category).
// Widths mirror the historical `[30,'*','*','*']`. These ids live in the LIGHTS
// section's field list but are DISTINCT from its content-line ids — see
// `LIGHT_FIXTURE_COLUMN_IDS` (riderLayout) and `buildLights`.
export const LIGHT_FIXTURE_COLUMNS: ListColumn<FixtureRow>[] = [
  { id: 'lights.fixtures.qty', label: 'Qty', width: 30, center: true, get: (r) => String(r.qty) },
  { id: 'lights.fixtures.model', label: 'Model', width: '*', get: (r) => r.model },
  { id: 'lights.fixtures.description', label: 'Description', width: '*', get: (r) => r.description },
  { id: 'lights.fixtures.notes', label: 'Notes', width: '*', get: (r) => r.notes },
];

/**
 * Resolve the ACTIVE table columns for a section from the persisted layout.
 * Reads section.fields (each column is a field in the editor):
 * - no fields array / empty list (legacy layout saved before column editing)
 *   → ALL columns, in the engine's default order (back-compat);
 * - otherwise the ENABLED columns, in the layout's field order, restricted to
 *   the known column set (spacer/text/unknown ids the editor may add are
 *   ignored for the table).
 */
function activeListColumns<T>(L: PdfLayout, sectionId: PdfSectionId, all: ListColumn<T>[]): ListColumn<T>[] {
  const sec = L.sections.find((s) => s.id === sectionId);
  if (!sec || !Array.isArray(sec.fields) || sec.fields.length === 0) return all;
  const fields = sec.fields; // captured so the callbacks below keep the array type
  // Legacy guard: a saved field list carrying NONE of the known column ids
  // (e.g. a Backline layout saved before the columns existed, holding only the
  // obsolete 'backline.table' id) is treated like "no list at all" → ALL
  // columns, rather than a table with zero columns. NOTE this does NOT break the
  // legitimate "uncheck every column" case: that list still CONTAINS the known
  // ids (with enabled:false), so it is respected and yields an empty table.
  const known = new Set(all.map((c) => c.id));
  if (!fields.some((f) => known.has(f.id))) return all;
  const order = fields.map((f) => f.id);
  return all
    .filter((c) => order.includes(c.id))
    .sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    .filter((c) => {
      const f = fields.find((x) => x.id === c.id);
      return f ? f.enabled !== false : true;
    })
    // Per-column WIDTH override (v0.644): a numeric `field.width` persisted by
    // the Rider Layout editor wins over the column's default width ('*' / a
    // number / 'auto'). Applied HERE so the builder's `widths[]` (built from
    // `cols.map(c => c.width)`) stays in mirror with the visible columns — no
    // separate widths array to keep in sync. Absent → default (no regression).
    .map((c) => {
      const w = fields.find((x) => x.id === c.id)?.width;
      return typeof w === 'number' ? { ...c, width: w } : c;
    });
}

// Translate an input/output/backline-list column header via i18n (pdf.col.*).
function colLabel(id: string): string {
  const map: Record<string, string> = {
    'input-list.ch': 'col.ch', 'input-list.track': 'col.track', 'input-list.rack': 'col.rack',
    'input-list.mic': 'col.mic', 'input-list.connection': 'col.connection', 'input-list.transmitter': 'col.transmitter',
    'input-list.stand': 'col.stand', 'input-list.provided': 'col.provided', 'input-list.notes': 'col.notes',
    'output-list.rack': 'col.rack', 'output-list.ch': 'col.ch', 'output-list.track': 'col.track',
    'output-list.type': 'col.type', 'output-list.notes': 'col.notes',
    'backline.qty': 'col.qty', 'backline.type': 'col.type', 'backline.item': 'col.description',
    'backline.model': 'col.model', 'backline.provided': 'col.provided', 'backline.notes': 'col.notes',
    'mic-stands.qty': 'col.qty', 'mic-stands.type': 'col.type', 'mic-stands.boom': 'col.boom',
    'mic-stands.destination': 'col.destination', 'mic-stands.provided': 'col.provided',
    'lights.fixtures.qty': 'col.qty', 'lights.fixtures.model': 'col.model',
    'lights.fixtures.description': 'col.description', 'lights.fixtures.notes': 'col.notes',
  };
  return map[id] ? t('pdf.' + map[id]) : id;
}

// Role ordering of the people lists (main → session → guest, then `order`,
// then `name`) lives in ONE place now: `../lib/peopleSort` (compareMusicians /
// compareCrew). It used to be duplicated here AND missing from the two preview
// checkbox lists — which is exactly how a `session` ended up listed after a
// `main`. Do not re-introduce a local comparator: import it.
// Guard: src/pages/__tests__/RiderPage.peopleOrder.test.tsx


// Resolve a server-relative or absolute image path (e.g. /static/uploads/..webp)
// into a PNG data URL so server-side pdfmake can embed it. Returns undefined on
// failure. Data URLs pass through (WebP/GIF/AVIF re-encoded to PNG). Shared by
// the band-logo loader, the show-rider preview, and backline-image resolution.
// (2026-08-11)
export async function resolveImageToDataUrl(u: string): Promise<string | undefined> {
  try {
    const res = await fetch(u, { credentials: 'include' });
    if (!res.ok) return undefined;
    const blob = await res.blob();
    if (blob.type === 'image/webp' || blob.type === 'image/gif' || blob.type === 'image/avif') {
      const url = URL.createObjectURL(blob);
      try {
        return await new Promise<string | undefined>((resolve) => {
          const img = new Image();
          const timer = setTimeout(() => { URL.revokeObjectURL(url); resolve(undefined); }, 8000);
          img.onload = () => {
            clearTimeout(timer);
            URL.revokeObjectURL(url);
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width || 400;
              canvas.height = img.height || 400;
              const ctx = canvas.getContext('2d');
              if (!ctx) { resolve(undefined); return; }
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL('image/png'));
            } catch { resolve(undefined); }
          };
          img.onerror = () => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(undefined); };
          img.src = url;
        });
      } catch {
        URL.revokeObjectURL(url);
        return undefined;
      }
    }
    return await new Promise<string | undefined>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

// Re-encode a non-PNG/JPEG data URL (WebP/GIF/AVIF) to PNG via a canvas.
export async function reencodeDataUrlToPng(dataUrl: string): Promise<string | undefined> {
  return new Promise((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => resolve(undefined), 8000);
    img.onload = () => {
      clearTimeout(timer);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width || 400;
        canvas.height = img.height || 400;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(undefined); return; }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch { resolve(undefined); }
    };
    img.onerror = () => { clearTimeout(timer); resolve(undefined); };
    img.src = dataUrl;
  });
}

// Produce the embeddable image for a stage plot, for EITHER type:
//  - kind === 'image'  → its stored PNG/JPEG data URL, passed straight through
//    (already a data URL, pdfmake-safe — resolve/re-encode if it somehow isn't).
//  - kind === 'canvas' (default) → rasterize the editable SVG to a PNG data URL.
// Returns null on failure (caller falls back to no stage-plot section).
export async function stagePlotToPdfImage(plot: { kind?: 'canvas' | 'image'; imageUrl?: string }): Promise<string | null> {
  if (!plot) return null;
  if (plot.kind === 'image' && plot.imageUrl) {
    if (plot.imageUrl.startsWith('data:image/png') || plot.imageUrl.startsWith('data:image/jpeg')) {
      return plot.imageUrl;
    }
    if (plot.imageUrl.startsWith('data:')) {
      return (await reencodeDataUrlToPng(plot.imageUrl)) ?? null;
    }
    return (await resolveImageToDataUrl(plot.imageUrl)) ?? null;
  }
  // canvas type
  try {
    // Inline the StagePlotPro PNGs the composite DrumKit references as data
    // URIs first: svgToPngDataUrl loads the SVG in an <img>, which cannot
    // fetch external resources, so the drum photos would render blank otherwise.
    const svg = await inlineStagePlotImages(stagePlotToSvg(plot as any));
    return await svgToPngDataUrl(svg);
  } catch {
    return null;
  }
}

export function buildPdfDoc(
  bandName: string | undefined,
  riderDoc: { transport: string; activeMusicianIds: string[]; activeCrewIds: string[]; requirements?: Record<string, any> | null },
  config: ReturnType<typeof useConfig>['config'],
  musicians: ReturnType<typeof useMusicians>['musicians'],
  crew: ReturnType<typeof useCrew>['crew'],
  stagePlot?: StagePlot | null,
  stagePlotImage?: string | null,
  bandLogo?: string | null,
  // Diet now lives on the Team identity (team_members), not the rider card.
  // Map team_member id -> { diet, dietNotes, nonFoodAllergies } so the
  // hospitality PDF section keeps showing dietary restrictions even though
  // rider cards no longer carry `diet`. Falls back to the legacy `m.diet` on
  // the card if the id is unknown. (2026-08-26: nonFoodAllergies added for the
  // per-person non-food allergies field.)
  dietById?: Record<string, { diet?: string; dietNotes?: string; nonFoodAllergies?: string }>,
  // Per-show role overrides (rider_roles): memberId -> full role list for THIS
  // show. When a member is present, their roles REPLACE the band-wide tags in
  // the travel party (a bassist can be tour manager on this show only).
  // Members absent from the map keep their band tags. (2026-08-07)
  showRoles?: Record<string, string[]>,
  // Management / Advancing contact: personal info lives on the TEAM page
  // (team_members), NOT on the rider crew cards. When provided, this wins over
  // the legacy crew-card fallback. (2026-08-07)
  teamManager?: { name?: string; email?: string; phone?: string } | null,
  // Per-show show duration in minutes (2026-08-09): the show is the central
  // entity — a festival set is shorter than a club headline. When provided it
  // overrides the band-level Schedule config (cfgGeneral.duration).
  showDuration?: number | null,
  // Layout override (2026-08-09): reorder / toggle / retitle / restyle the
  // rider sections. When omitted, createDefaultPdfLayout() reproduces the
  // exact original hard-coded behaviour.
  layout?: PdfLayout | null,
  // Tour name (2026-08-11): when the rider is generated for a show that
  // belongs to a tour, the tour name is rendered under the logo (before the
  // title) — e.g. "Summer Tour 2026".
  tourName?: string | null,
  // Light plots (2026-10-06): the multi-plot files resolved to embeddable
  // data URLs, in the SAME order as `config.lights[transport].plots`. When
  // omitted (legacy callers, layout editors), only the legacy single
  // `imageData` is emitted — behaviour unchanged. The names become captions.
  lightPlotImages?: LightPlotRender[] | null,
): TDocumentDefinitions {
  const transport = (riderDoc.transport as 'van' | 'plane' | 'tourbus' | 'train') || '';
  // LAYOUT-DRIVEN ENGINE: sections, fixed texts and style come from a
  // PdfLayout. With no layout passed, createDefaultPdfLayout() reproduces the
  // exact hard-coded behaviour below; mergePdfLayouts() layers a persisted
  // admin override (reorder / toggle / retitle / restyle) on top.
  const L = layout ? mergePdfLayouts(createDefaultPdfLayout(), layout) : createDefaultPdfLayout();
  // Translate the DEFAULT fixed texts (title / contact / intro / footer /
  // header templates) via i18n so the PDF respects the active language. An
  // admin override saved in the layout is preserved verbatim (user data).
  const D = DEFAULT_PDF_LAYOUT_TEXTS;
  if (L.texts.titleSuffix === D.titleSuffix) L.texts.titleSuffix = t('pdf.titleSuffix');
  if (L.texts.contactLabel === D.contactLabel) L.texts.contactLabel = t('pdf.contactLabel');
  if (L.texts.introText === D.introText) L.texts.introText = t('pdf.introText');
  if (L.texts.versionTemplate === D.versionTemplate) L.texts.versionTemplate = t('pdf.versionTemplate');
  for (const k of ['footerLeftTemplate', 'footerCenterTemplate', 'footerRightTemplate', 'headerLeftTemplate', 'headerCenterTemplate', 'headerRightTemplate'] as const) {
    if (L.texts[k] === D[k]) L.texts[k] = t('pdf.' + k);
  }
  const secMap = new Map<PdfSectionId, { enabled: boolean; titleOverride: string | null; pageBreakBefore: boolean }>();
  for (const s of L.sections) {
    secMap.set(s.id, { enabled: s.enabled, titleOverride: s.title || null, pageBreakBefore: !!s.pageBreakBefore });
  }
  // Section heading text (title override wins over the default) and page-break flag.
  const secTitle = (id: PdfSectionId, fallback: string): string => secMap.get(id)?.titleOverride || fallback;
  const secPbb = (id: PdfSectionId): boolean => !!secMap.get(id)?.pageBreakBefore;
  // UPPERCASE per section (2026-10-17, user request): cascade global
  // `L.style.uppercase` → `sections[].styleOverride.uppercase`. Needed as an
  // explicit helper because most table ROWS are PRE-COMPUTED outside the
  // section loop (where `secStyle` still holds the GLOBAL style).
  const secUppercase = (id: PdfSectionId): boolean => {
    const s = L.sections.find((x) => x.id === id);
    return s?.styleOverride?.uppercase ?? L.style.uppercase ?? false;
  };
  // SHOW EMPTY ROWS (2026-10-18, user): OUTPUT list only — the Input list keeps
  // its historical "holes" rule unconditionally.
  const secShowEmpty = (id: PdfSectionId): boolean => {
    const s = L.sections.find((x) => x.id === id);
    return s?.styleOverride?.showEmptyRows ?? false;
  };
  // Field-level visibility: a section with a `fields` list renders ONLY its
  // enabled fields, in list order (editor "place each field as you want").
  // No fields list / unknown field id → render (back-compat with legacy layouts).
  const fieldEnabled = (sectionId: PdfSectionId, fieldId: string): boolean => {
    const sec = L.sections.find((s) => s.id === sectionId);
    if (!sec || !Array.isArray(sec.fields) || sec.fields.length === 0) return true;
    const f = sec.fields.find((x) => x.id === fieldId);
    return f ? f.enabled !== false : true;
  };
  // ---- Per-FIELD style (v0.558) -------------------------------------------
  // Same idea one level deeper than applySectionStyle: `fieldStyleOverride(f)`
  // returns ONLY the keys the field explicitly overrides (the legacy flat
  // fontSize/bold/italic/align of a free-text field mapped onto their style
  // key, `field.styleOverride` winning). The inherited parts already come from
  // `defaultStyle` (global) and `applySectionStyle` (section) — so a field only
  // stamps its OWN keys, and it does so BEFORE applySectionStyle runs: that
  // helper never overwrites a value a node already carries, which is what makes
  // the cascade global → section → FIELD (field wins). Declared here (before
  // every builder) because the Input/Output/Rack tables resolve their column
  // overrides while the table body is being built.
  const FIELD_STYLE_KEYS = ['fontFamily', 'bodyFontSize', 'textColor', 'fieldBold', 'fieldItalic', 'fieldAlign'] as const;
  const applyFieldStyle = (nodes: Content[], ov: Partial<PdfLayoutStyle> | null): Content[] => {
    if (!ov || !FIELD_STYLE_KEYS.some((k) => ov[k] !== undefined)) return nodes;
    const stamp = (node: any): void => {
      if (ov.fontFamily !== undefined) node.font = ov.fontFamily;
      if (ov.bodyFontSize !== undefined) node.fontSize = ov.bodyFontSize;
      if (ov.textColor !== undefined) node.color = ov.textColor;
      if (ov.fieldBold !== undefined) node.bold = ov.fieldBold;
      if (ov.fieldItalic !== undefined) node.italics = ov.fieldItalic;
      if (ov.fieldAlign !== undefined) node.alignment = ov.fieldAlign;
    };
    const walk = (node: any): void => {
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (!node || typeof node !== 'object') return;
      if (node.text !== undefined) {
        stamp(node);
        // Rich text (`text: [{ text, bold }, …]` — the hLabel / pushBullet
        // "LABEL: value" segments) → the label segment must follow too,
        // otherwise a bold label stays bold on a field set to non-bold.
        if (Array.isArray(node.text)) {
          node.text.forEach((seg: any) => { if (seg && typeof seg === 'object') stamp(seg); });
        }
      }
      for (const k of ['stack', 'columns', 'ul', 'ol']) {
        if (node[k]) walk(node[k]);
      }
      if (node.table && node.table.body) walk(node.table.body);
    };
    walk(nodes);
    return nodes;
  };
  // The layout FIELD definition of the CURRENT section, by id — for the
  // builders that emit one monolithic block per column (Input / Output / Rack
  // Usage tables) where the field object itself is not at hand.
  const fieldById = (sectionId: PdfSectionId, fid: string): PdfLayoutField | undefined =>
    L.sections.find((s) => s.id === sectionId)?.fields?.find((f) => f.id === fid);
  const fieldOvById = (sectionId: PdfSectionId, fid: string): Partial<PdfLayoutStyle> | null =>
    fieldStyleOverride(fieldById(sectionId, fid));
  // Transport label of the "(version: van, 2 horns)" subtitle. ALL FOUR modes
  // have one (user 2026-09-15): van used to yield an empty label, so a van
  // rider printed "(version: )" with nothing after the colon.
  const TRANSPORT_LABELS: Record<string, string> = {
    van: t('pdf.transport.van'),
    plane: t('pdf.transport.plane'),
    tourbus: t('pdf.transport.tourbus'),
    train: t('pdf.transport.train'),
  };
  const transportLabel = TRANSPORT_LABELS[transport] || '';
  // Selected musicians, sorted main → session → guest, then by `order`, then by
  // `name` — the SAME ordering as the Musicians page cards AND as the preview's
  // checkbox list (single source: `lib/peopleSort`). Everything derived from
  // `selected` (travel party, crew phrase, backline, mic stands, provided,
  // hospitality, the input/output list loops) inherits this order.
  const selected = musicians
    .filter((m) =>
      (riderDoc.activeMusicianIds || []).includes(m.id),
    )
    .sort(compareMusicians);

  // Defensive resolution: the saved config may predate the current schema and
  // miss a section or array (e.g. older bands without hospitality.catering).
  // Default every section/array so buildPdfDoc never throws on missing data.
  const defSection = (section: any): any => section && typeof section === 'object' ? section : {};
  // Prefer the rider instance's own requirements (show-specific), else derive
  // from the band config for the chosen transport mode.
  const req = riderDoc.requirements;
  const sec = (name: string): any => {
    const reqVal = req && req[name];
    if (reqVal) return defSection(reqVal);
    // No transport chosen yet: emit an empty preset rather than defaulting to
    // 'van' (Defaults-Spec P8) — the PDF renders whatever the band configured.
    if (!transport) return {};
    // The band config section is either a per-transport preset bundle (has
    // van/plane/tourbus/train keys) or a plain value (e.g. `rider`). Index it
    // by the active transport, falling back to the section as a whole.
    const section = config[name as keyof AppConfig] as Record<string, unknown> | undefined;
    const perTransport = section?.[transport];
    return defSection(perTransport ?? section);
  };
  const cfgSound = sec('sound');
  const cfgIem = sec('iem');
  // Provider fields (band vs venue) may be missing from riders created before
  // they existed (v72). Merge them from the band config so the PDF always
  // reflects "provided by the band" when the band brings the gear. (2026-08-09)
  const cfgSoundBase = defSection(transport ? (config.sound?.[transport as TransportType] ?? config.sound) : {});
  const cfgIemBase = defSection(transport ? (config.iem?.[transport as TransportType] ?? config.iem) : {});
  if (cfgSound.fohProvider == null && cfgSoundBase.fohProvider != null) cfgSound.fohProvider = cfgSoundBase.fohProvider;
  if (cfgSound.paProvider == null && cfgSoundBase.paProvider != null) cfgSound.paProvider = cfgSoundBase.paProvider;
  if (cfgSound.monitorProvider == null && cfgSoundBase.monitorProvider != null) cfgSound.monitorProvider = cfgSoundBase.monitorProvider;
  if (cfgIem.provider == null && cfgIemBase.provider != null) cfgIem.provider = cfgIemBase.provider;
  const cfgHospitality = {
    catering: [] as string[],
    drinks: [] as string[],
    dressingRooms: 'TBD',
    nonFoodAllergies: '',
    various: '',
    ...sec('hospitality'),
  };
  const cfgLights = sec('lights');
  const cfgGeneral = sec('general');
  const cfgSpecs = sec('specs');
  const cfgStageDesign = sec('stageDesign');

  const M = [0, 0, 0, 2] as [number, number, number, number];
  const M4 = [0, 0, 0, 4] as [number, number, number, number];
  // Per-section style: `secStyle` holds the style of the section CURRENTLY
  // being built (global style + that section's styleOverride). It is set in
  // the section loop just before the section's builder runs; everything INSIDE
  // a builder must read `secStyle`, while the document-wide pieces (page
  // margins, running header/footer, defaultStyle) keep reading the GLOBAL
  // `L.style` — a section override must never leak into the footer.
  let secStyle: PdfLayoutStyle = L.style;
  // Section heading: centered UPPERCASE bold title with a full-width rule
  // underneath (like the Kadebostany reference). The rule is drawn as a
  // canvas line spanning the usable width; size/color/width come from the
  // layout style (A4 595 - 80 margins = 515pt by default). The bold weight
  // follows the style's `headingBold` option (default true = historical look).
  const sectionTitle = (text: string, opts?: { pageBreakBefore?: boolean }): Content => ({
    stack: [
      { text: text.toUpperCase(), alignment: 'center', fontSize: secStyle.headingFontSize, bold: secStyle.headingBold !== false, color: secStyle.headingColor, margin: [0, 14, 0, 3] as [number, number, number, number] },
      { canvas: [{ type: 'line' as const, x1: 0, y1: 0, x2: secStyle.ruleWidth, y2: 0, lineWidth: 0.8, lineColor: secStyle.ruleColor }], margin: [0, 0, 0, 8] as [number, number, number, number] },
    ],
    ...(opts?.pageBreakBefore ? { pageBreak: 'before' as const } : {}),
  });

  // ---- Shared table look (all rider tables) -------------------------------
  // pdfmake is handed the docDef as JSON (`POST /api/pdf` → JSON.stringify), and
  // JSON CANNOT carry functions: the previous `layout: { hLineColor: () => …,
  // paddingTop: () => 3 }` was silently stripped, so EVERY rider table fell back
  // to pdfmake's built-in default layout — a heavy 1pt BLACK grid on every line
  // (and, for the empty channel rows of the Input List, a ~5pt black band that
  // reads as a "bold line"). The look below is JSON-safe on purpose:
  //   * `layout: 'lightHorizontalLines'` — a BUILT-IN layout NAME (a string
  //     survives JSON; pdfmake resolves it server-side). Same look as the
  //     daysheet / tour-book tables: no vertical lines, no outer frame, 1pt
  //     #aaa lines between rows and a crisp 2pt rule under the header.
  //   * header fill / bold / zebra go on the CELLS (`fillColor` is serializable),
  //     never in a layout function.
  //   * cell `margin` is the only vertical padding pdfmake 0.3.x honours
  //     (minRowHeight is ignored) — kept identical for header and data cells so
  //     the columns line up and every row has the same height.
  const TABLE_LAYOUT = 'lightHorizontalLines';
  // The Input List carries up to 9 columns. With the 8 pt-per-side horizontal
  // padding of `lightHorizontalLines` (16 pt of overhead per column boundary,
  // i.e. 16·(n−1) = 128 pt for 9 columns) the table CANNOT fit the 515 pt usable
  // width — even at the smallest widths that keep every header label. It
  // therefore uses a DENSE named layout, registered SERVER-SIDE as
  // `bandzTableDense` (server/routes/pdf.js): the SAME look (no vertical lines,
  // no outer frame, 1 pt #aaa row lines, 2 pt rule under the header) but 3 pt
  // padding per side → boundary overhead 6·(n−1) = 48 pt. ⚠️ It MUST be sent as a
  // STRING: a client-side layout OBJECT carrying functions is silently stripped
  // by the `JSON.stringify` of POST /api/pdf and the table falls back to
  // pdfmake's default heavy black grid. The other tables keep
  // `lightHorizontalLines` (they fit). Judge: scripts/qa-rider-overflow-words.ts.
  const INPUT_LIST_TABLE_LAYOUT = 'bandzTableDense';
  const CELL_MARGIN = [4, 0, 4, 0] as [number, number, number, number];
  const ZEBRA_FILL = '#f7f8f9';
  /**
   * CENTER a rider table narrower than the usable page width (v0.651).
   *
   * Why a wrapper and not `alignment`: pdfmake 0.3.x IGNORES `alignment` on a
   * table node (verified by rendering — the table stays flush left), and a table
   * whose `widths` contain a '*' STRETCHES to the full usable width on its own
   * (columnCalculator case 3). So ONLY a table with NO '*' column renders at its
   * NATURAL width; when that is narrower than the usable width it used to sit
   * flush left with dead space on the right (real case: the SAMAEL Mic Stands
   * table = 348 pt of 515 pt). Wrapping it in a 3-cell `columns` row — two
   * flexible EMPTY flanks around an 'auto' middle cell that sizes to the table's
   * natural width — centers it, without altering the table's internal column
   * widths (proven by rendering: same widths, shifted right by the left margin).
   *
   * A table WITH a '*' column is returned UNCHANGED: it already fills the page,
   * so the 35 full-width tables never move.
   *
   * ⚠️ The wrapper is plain JSON (no layout OBJECT / no function) so it survives
   * the `JSON.stringify` of POST /api/pdf.
   */
  const centerTableIfNarrow = (node: Content, widths: (number | string)[]): Content => {
    const hasStar = widths.some((w) => w === '*' || w === 'star' || w == null);
    if (hasStar) return node;
    return {
      columns: [
        { width: '*', text: '' },
        { width: 'auto', ...(node as any) },
        { width: '*', text: '' },
      ],
      columnGap: 0,
    } as Content;
  };
  /** Build a rider table node and center it when it is narrower than the page
   *  (a no-op for the full-width tables — see `centerTableIfNarrow`). */
  const riderTableNode = (table: any, layout: string, margin: [number, number, number, number]): Content =>
    centerTableIfNarrow({ table, layout, margin } as Content, (table?.widths || []) as (number | string)[]);
  /**
   * Per-FIELD style keys stamped onto a node when its field overrides them
   * (v0.558). Every key is spread LAST so a field override wins over the
   * cell/row defaults (header bold, centered column, the 8pt occupant list…).
   */
  const fieldStyleProps = (ov?: Partial<PdfLayoutStyle> | null): Record<string, unknown> => {
    if (!ov) return {};
    return {
      ...(ov.fontFamily !== undefined ? { font: ov.fontFamily } : {}),
      ...(ov.bodyFontSize !== undefined ? { fontSize: ov.bodyFontSize } : {}),
      ...(ov.textColor !== undefined ? { color: ov.textColor } : {}),
      ...(ov.fieldBold !== undefined ? { bold: ov.fieldBold } : {}),
      ...(ov.fieldItalic !== undefined ? { italics: ov.fieldItalic } : {}),
      ...(ov.fieldAlign !== undefined ? { alignment: ov.fieldAlign } : {}),
    };
  };
  /** Header cell: bold + light fill, same padding as the data cells. */
  const headCell = (text: string, opts?: { center?: boolean; styleOverride?: Partial<PdfLayoutStyle> | null }): any => ({
    // Column headers are titles → render MAJUSCULE (same convention as
    // sectionTitle / hLabel). Only the rendered text is uppercased — the
    // i18n values (pdf.col.*, pdf.rackUsage.*) are left untouched.
    text: text.toUpperCase(),
    bold: true,
    fillColor: secStyle.tableHeaderFill,
    margin: CELL_MARGIN,
    ...(opts?.center ? { alignment: 'center' } : {}),
    ...fieldStyleProps(opts?.styleOverride),
  });
  /**
   * Data cell. A non-breaking space keeps EMPTY cells (unused channels between
   * the first and the last used one — the Input List "holes") at the same line
   * height as filled ones, so no row renders as a fat black bar. `zebra` adds
   * the light alternating fill.
   */
  const dataCell = (value: unknown, opts?: { center?: boolean; zebra?: boolean; fontSize?: number; styleOverride?: Partial<PdfLayoutStyle> | null; uppercase?: boolean; highlight?: string }): any => ({
    // UPPERCASE option (2026-10-17, setlist pattern; PER-SECTION since user
    // request same day): the caller passes `uppercase` (resolved from the
    // section's own styleOverride via secUppercase) because most table rows
    // are pre-computed outside the section loop; tables built INSIDE the loop
    // fall back to `secStyle.uppercase`. Applied at render, never in the
    // stored data nor the i18n values; headers are already uppercase.
    text: (value === undefined || value === null || value === '') ? '\u00A0' : ((opts?.uppercase ?? secStyle.uppercase) ? String(value).toUpperCase() : String(value)),
    margin: CELL_MARGIN,
    ...(opts?.center ? { alignment: 'center' } : {}),
    ...(opts?.fontSize ? { fontSize: opts.fontSize } : {}),
    // VENUE HIGHLIGHT (2026-10-18): wins over the zebra fill — a highlighted
    // row keeps ONE flat fill, never zebra-over-highlight.
    ...(opts?.highlight ? { fillColor: opts.highlight } : opts?.zebra ? { fillColor: ZEBRA_FILL } : {}),
    ...fieldStyleProps(opts?.styleOverride),
  });
  // Venue highlight colour (undefined = disabled) — global style only (a
  // highlight is a document-wide reading aid, not a per-section style).
  const venueHi = (): string | undefined => {
    if (L.style.venueHighlight !== true) return undefined;
    return L.style.venueHighlightColor || '#FFF9C4';
  };
  // Background tint for a free-text (hLabel) LINE whose provider is venue.
  // pdfmake renders `background` on a text node as a line-box fill.
  const lineHi = (provider?: string): string | undefined =>
    venueHi() && provider === 'venue' ? venueHi() : undefined;

  // Pre-calculate crew + totals (used in multiple sections)
  const allCrew = crew || [];
  // Selected crew, sorted main → session, then by `order` (name as last
  // resort) — the SAME ordering as the Crew page cards AND as the preview's
  // checkbox list (single source: `lib/peopleSort`). Everything derived
  // from `selectedCrew` (crew phrase, rooming, backline, hospitality) inherits
  // this order.
  const selectedCrew = allCrew
    .filter(c => riderDoc.activeCrewIds?.includes(c.id))
    .sort(compareCrew);
  // Each crew card represents exactly one person (Count field removed).
  const crewCount = selectedCrew.length;
  const totalPeople = selected.length + crewCount;

  // Input/Output lists are built from EVERYBODY selected — musicians AND crew.
  // Bug corrigé (user 2026-09-18) : Chopiou (ingé retours KADEBOSTANY, carte
  // CREW) a des pistes sur les canaux 27-28 qui n'apparaissaient jamais, car les
  // boucles ne lisaient que `selected` (musiciens). Les crew partagent EXACTEMENT
  // le modèle instruments/pistes des musiciens (types.ts : CrewMember.instruments
  // + Instrument.tracks + inputListChannel/ioChannel/direction).
  // ⚠️ Déclaré ICI (après selectedCrew) : le référencer plus haut = TDZ.
  const selectedPeople = [...selected, ...selectedCrew] as Array<
    (typeof selected)[number] | (typeof selectedCrew)[number]
  >;
  const personName = (p: { name?: string; fonction?: string; role?: string }): string =>
      p.name || p.fonction || p.role || 'Unnamed';
    // A monitor engineer (tag 'mon' / 'monitor' / 'monitor engineer') — their
    // IEM and wedges are for PFL (pre-fade listen), not the musical mix. Detected
    // through the same canonical-tag logic as the travel party, honoring the
    // per-show role override (rider_roles). (2026-10-06)
    const isMonitorEngineer = (m: { id?: string; tags?: string[]; role?: string }): boolean => {
      const showTags = showRoles && m.id ? showRoles[m.id] : undefined;
      const tags: string[] = (showTags !== undefined ? showTags : (m.tags && m.tags.length ? m.tags : (m.role ? [m.role] : [])))
        .map((t) => canonTag(String(t).toLowerCase()));
            return tags.includes('mon');
    };
  // Group Manager: prefer the Team-page identity (personal info lives there),
  // fall back to the 'manager' tag on a crew card (new model), then the legacy
  // `isManager` flag for cards saved before tags existed.
  const crewManager = crew.find((c) => (c.tags || []).includes('manager') || c.isManager);
  const manager = teamManager && (teamManager.name || teamManager.email || teamManager.phone)
    ? { name: teamManager.name, email: teamManager.email, phone: teamManager.phone }
    : crewManager;

  // Derive a "version detail" like the reference: transport + brass/horn count
  // e.g. "van, 2 horns". With no transport chosen the transport part is omitted
  // (never default to 'van').
  const BRASS = ['horn', 'trumpet', 'trombone', 'sax', 'saxophone', 'flugel', 'cornet', 'tuba', 'euphonium', 'brass'];
  let brassCount = 0;
  for (const m of selected) {
    for (const inst of (m.instruments || [])) {
      const n = (inst.name || '').toLowerCase();
      if (BRASS.some((b) => n.includes(b))) brassCount++;
    }
  }
  const detailParts: string[] = [];
  if (transportLabel) detailParts.push(transportLabel.toLowerCase());
  if (brassCount > 0) detailParts.push(`${brassCount} horns`);
  const versionDetail = detailParts.join(', ');

  const content: Content[] = [];

  // === GENERAL INFO (top, after contact — reference flow) ===
  // Intro sentence + "General Information" heading are emitted by the
  // layout-driven builder below (texts.introText / section 'general-info').

  // Crew breakdown phrase: "2 sound engineers (FOH, MON), 1 guitar tech, 1 tour manager"
  // Keys MUST cover the canonical CrewRole vocabulary used by CrewEditPage /
  // ShowDetailPage tech roles (driver, foh, guest, lights, manager, mon, other,
  // stagehand, tour manager, video, guitar tech, drum tech, keys tech) —
  // anything missing falls back to "No role" in the travel party. The legacy
  // single-word aliases (ld / lighting / tour) are kept for older cards.
  const crewRoleMap: Record<string, string> = {
    foh: t('pdf.crew.soundEngineer'), mon: t('pdf.crew.soundEngineer'), ld: t('pdf.crew.lightingTechnician'),
    lights: t('pdf.crew.lightingTechnician'), lighting: t('pdf.crew.lightingTechnician'),
    manager: t('pdf.crew.tourManager'), 'tour manager': t('pdf.crew.tourManager'), tour: t('pdf.crew.tourManager'),
    driver: t('pdf.crew.driver'), stagehand: t('pdf.crew.stagehand'), video: t('pdf.crew.videoTechnician'), guest: t('pdf.crew.guest'),
    other: t('pdf.crew.member'),
    'guitar tech': t('pdf.crew.guitarTech'), 'drum tech': t('pdf.crew.drumTech'), 'keys tech': t('pdf.crew.keysTech'),
  };
  // Legacy / synonym tags used by older or imported cards, mapped onto the
  // canonical CrewRole tags so the group label AND the uppercase suffix stay
  // consistent (e.g. 'sound' → FOH, 'monitor' → MON, 'light' → LD).
  const CREW_TAG_ALIASES: Record<string, string> = {
    sound: 'foh', 'sound engineer': 'foh', 'foh engineer': 'foh', audio: 'foh',
    monitor: 'mon', 'monitor engineer': 'mon',
    light: 'lights', ld: 'lights', lighting: 'lights', 'lighting technician': 'lights',
    tour: 'tour manager', tourmanager: 'tour manager', tm: 'tour manager',
  };
  const canonTag = (tag: string): string => CREW_TAG_ALIASES[tag] || tag;
  // Tags shown in UPPERCASE inside the suffix — same treatment as FOH/MON.
  // The principal tag decides the group label; extra tags add to the suffix.
  const TAG_SHORT: Record<string, string> = {
    foh: t('pdf.crew.tag.foh'), mon: t('pdf.crew.tag.mon'), lights: t('pdf.crew.tag.lights'),
    'guitar tech': t('pdf.crew.tag.guitarTech'), 'drum tech': t('pdf.crew.tag.drumTech'), 'keys tech': t('pdf.crew.tag.keysTech'),
  };
  const crewGroups = new Map<string, { count: number; tags: Set<string> }>();
  for (const c of selectedCrew) {
    // Per-show override (rider_roles) replaces the band tags for this member.
    const showTags = showRoles && showRoles[c.id];
    const ctags: string[] = (showTags !== undefined ? showTags : (c.tags && c.tags.length ? c.tags : (c.role ? [c.role] : []))).map(t => canonTag(String(t).toLowerCase()));
    // First recognized tag = the group label (e.g. 'mon' → 'sound engineer').
    const principalIdx = ctags.findIndex(t => crewRoleMap[t]);
    let label: string;
    if (principalIdx >= 0) {
      label = crewRoleMap[ctags[principalIdx]];
    } else {
      // No recognized tag: report the card's own free-text role (imported /
      // legacy cards hold values like "Front of House Engineer") rather than
      // collapsing it to a generic "No role".
      const roleText = showTags === undefined ? String(c.role || '').trim() : '';
      const roleKey = canonTag(roleText.toLowerCase());
      label = roleKey && crewRoleMap[roleKey] ? crewRoleMap[roleKey] : (roleText || t('crew.noRole'));
    }
    const tags = new Set<string>();
    ctags.forEach((t, i) => {
      // foh/mon/lights are kept in the suffix even as the principal tag (legacy).
      if (i === principalIdx && t !== 'foh' && t !== 'mon' && t !== 'lights') return;
      if (TAG_SHORT[t]) tags.add(TAG_SHORT[t]);
      else if (crewRoleMap[t]) tags.add(t);
    });
    const g = crewGroups.get(label) || { count: 0, tags: new Set() };
    g.count += 1; // each crew card = one person
    tags.forEach(t => g.tags.add(t));
    crewGroups.set(label, g);
  }
  const crewPhrase = Array.from(crewGroups.entries())
    .map(([label, g]) => {
      const tagSuffix = g.tags.size ? ` (${Array.from(g.tags).join(', ')})` : '';
      return `${g.count} ${label}${g.count > 1 ? 's' : ''}${tagSuffix}`;
    })
    .join(', ');

  // Musicians can also hold extra roles for the band (e.g. bassist is also the
  // tour manager) — show them as a suffix after the musician count, same spirit
  // as crew tags. The total count stays `totalPeople` (no double counting).
  const musicianTagCounts = new Map<string, number>();
  for (const m of selected) {
    // Per-show override (rider_roles) replaces the band tags for this member.
    // (showRoles may be null from a caller — never leak null into the loop.)
    const showTags = (showRoles && m.id) ? showRoles[m.id] : undefined;
    const tags = showTags !== undefined ? showTags : (m.tags || []);
    for (const t of tags) {
      musicianTagCounts.set(t, (musicianTagCounts.get(t) || 0) + 1);
    }
  }
  const musicianTagSuffix = musicianTagCounts.size
    ? ` (${Array.from(musicianTagCounts.entries()).map(([t, n]) => `${n} ${t}`).join(', ')})`
    : '';

  const travelPartyText = t('pdf.field.travelParty', { total: totalPeople, musicians: selected.length, suffix: musicianTagSuffix, crew: crewPhrase ? `, ${crewPhrase}` : '' });
  // Minutes helper: "3 hours" when divisible by 60, else "45 minutes" (matches
  // the Kadebostany reference rider).
  const fmtMinutes = (m: number): string => {
    if (m % 60 === 0) {
      const h = Math.round(m / 60);
      return h > 1 ? t('pdf.minutes.hours', { count: h }) : t('pdf.minutes.hour', { count: h });
    }
    return t('pdf.minutes.minutes', { count: m });
  };
  // Renders 'Label: value' as a rich text node with the label in bold and
  // UPPERCASE. pdfmake supports inline text arrays: [{ text: 'LABEL: ', bold:
  // true }, 'value']. The uppercasing happens at RENDER TIME only: the same
  // i18n keys feed the web UI, which keeps its normal case (2026-09-16).
  //
  // NEW-LINE CONVENTION (2026-08-11, user): if the VALUE starts with an empty
  // first line (e.g. "\n2 stagehands..."), the label goes on its own line and
  // the value starts on the line below — otherwise label and value share the
  // same line. This works for every free-text field (Security, Merchandising,
  // Local crew, Notes, IEM note, Lights notes, hospitality bullets...).
  // Declared before the field builders use it (no TDZ surprises).
  const hLabel = (text: string): Content => {
    const idx = text.indexOf(':');
    if (idx === -1) return { text };
    const label = text.slice(0, idx + 1).trim().toUpperCase();
    const rest = text.slice(idx + 1);
    if (/^\s*\n/.test(rest)) {
      // Value begins with an empty first line → label on its own line, value
      // below (the empty line itself is dropped).
      return {
        stack: [
          { text: label, bold: true, margin: [0, 0, 0, 1] },
          { text: rest.replace(/^\s*\n+/, ''), margin: [0, 0, 0, 0] },
        ],
      } as unknown as Content;
    }
    const trimmed = rest.trim();
    return {
      text: [
        { text: label, bold: true },
        trimmed ? ` ${trimmed}` : '',
      ],
    } as unknown as Content;
  };
  // Each general-info line is tagged with its field id so the layout engine
  // can enable/disable/reorder individual fields ("place each field as you
  // want"). Lines are emitted in the layout's field order, not code order.
  const generalFieldContent = new Map<string, Content[]>();
  const gAdd = (fieldId: string, ...nodes: Content[]) => {
    const arr = generalFieldContent.get(fieldId) || [];
    arr.push(...nodes);
    generalFieldContent.set(fieldId, arr);
  };
  // Travel party is a full "Label: value" phrase ("Travel party: 6 people on
  // the road: ...") → routed through hLabel so "TRAVEL PARTY:" renders bold +
  // UPPERCASE at render time while the value keeps its normal case.
  gAdd('general-info.travel-party', { ...hLabel(travelPartyText), margin: M });
  // The show duration may be overridden per show (festival set vs club headline):
  // `showDuration` param wins over the band-level Schedule config.
  const effectiveDuration = showDuration != null ? showDuration : (cfgGeneral.duration || 0);
  const hasSetupTimes = effectiveDuration > 0
    || cfgGeneral.installationTimeMinutes
    || cfgGeneral.soundcheckDurationMinutes
    || cfgGeneral.changeoverDurationMinutes
    || cfgGeneral.breakdownDurationMinutes;
  const setupFieldIds = ['general-info.show-duration', 'general-info.installation', 'general-info.soundcheck', 'general-info.changeover', 'general-info.breakdown'];
  if (hasSetupTimes && setupFieldIds.some((id) => fieldEnabled('general-info', id))) {
    if (effectiveDuration > 0) gAdd('general-info.show-duration', { ...hLabel(t('pdf.field.showDuration', { value: fmtMinutes(effectiveDuration) })), margin: M });
    if (cfgGeneral.installationTimeMinutes) gAdd('general-info.installation', { ...hLabel(t('pdf.field.installation', { value: fmtMinutes(cfgGeneral.installationTimeMinutes) })), margin: M });
    if (cfgGeneral.soundcheckDurationMinutes) gAdd('general-info.soundcheck', { ...hLabel(t('pdf.field.soundcheck', { value: fmtMinutes(cfgGeneral.soundcheckDurationMinutes) })), margin: M });
    if (cfgGeneral.changeoverDurationMinutes) gAdd('general-info.changeover', { ...hLabel(t('pdf.field.changeover', { value: fmtMinutes(cfgGeneral.changeoverDurationMinutes) })), margin: M });
    if (cfgGeneral.breakdownDurationMinutes) gAdd('general-info.breakdown', { ...hLabel(t('pdf.field.breakdown', { value: fmtMinutes(cfgGeneral.breakdownDurationMinutes) })), margin: M });
  }
  // NOTE: sound limit moved to the Sound section (user pref 2026-08-09).
  // Local crew (stagehands / techs on site).
  if (cfgGeneral.specialRequests) gAdd('general-info.special-requests', { ...hLabel(t('pdf.field.localCrew', { value: cfgGeneral.specialRequests })), margin: M });
  if (cfgGeneral.security) gAdd('general-info.security', { ...hLabel(t('pdf.field.security', { value: cfgGeneral.security })), margin: M });
  if (cfgGeneral.merchandise) gAdd('general-info.merchandise', { ...hLabel(t('pdf.field.merchandising', { value: cfgGeneral.merchandise })), margin: M });
  if (cfgGeneral.notes) gAdd('general-info.notes', { ...hLabel(t('pdf.field.notes', { value: cfgGeneral.notes })), margin: M4 });
  // --- Input/Output List (summary) ---
  // Rack name lookup (band's Audio & Backline config: global racks first, then
  // the ACTIVE transport preset's racks — a track resolves its rackId against
  // globalRacks, then the per-transport exception list).
  const rackNameById = new Map<string, string>();
  for (const r of config.globalRacks || []) if (r && r.id && !rackNameById.has(r.id)) rackNameById.set(r.id, r.name);
  if (transport) {
    const list = config.specs?.[transport]?.stageRacks || [];
    for (const r of list) if (r && r.id && !rackNameById.has(r.id)) rackNameById.set(r.id, r.name);
  }
  const rackLabel = (id?: string) => (id && rackNameById.get(id)) || '';

  const inputColumns = activeListColumns(L, 'input-list', INPUT_LIST_COLUMNS);
  // Per-COLUMN style override (a table column IS a layout field): resolved
  // once per render, applied to the column's header cell and every data cell.
  const inputColOv = new Map(inputColumns.map((c) => [c.id, fieldOvById('input-list', c.id)]));
  const inputListRows: any[] = [
    inputColumns.map((c) => headCell(colLabel(c.id), { center: c.center, styleOverride: inputColOv.get(c.id) })),
  ];

  // Aggregate tracks from SELECTED musicians only. The input list stops at
  // the LAST channel that has a track (no padding after it — user rule
  // 2026-08-07), but grows to fit the highest channel used (so a musician on
  // channels 27-28 yields a 28-row table). Tracks keep their channel
  // position; gaps between the first and last used channel stay empty.
  // Output tracks (returns/IEM/sends) go to the separate Output List below.
  // HÉRITAGE DES I/O POUR LES REMPLAÇANTS (2026-10-17, user : « Tim devrait pouvoir
  // utiliser les mêmes pistes que Zack car s'il est là c'est a priori pour remplacer
  // Zack »). Un track SANS canal (inputListChannel vide) dont l'instrument porte le
  // MÊME NOM que l'instrument d'un autre membre sélectionné (qui a lui des canaux)
  // HÉRITE du canal de la piste correspondante (par nom de piste, sinon par
  // position). Les canaux EXPLICITES gagnent toujours — l'héritage ne touche que
  // les pistes non numérotées. Même mécanique pour l'Output List.
  const ioInherit = new Map<string, { inputByTrack: Map<string, number>; inputOrder: number[]; outputByTrack: Map<string, number>; outputOrder: number[] }>();
  for (const m of selectedPeople) {
    for (const inst of m.instruments || []) {
      const key = (inst.name || '').trim().toLowerCase();
      if (!key) continue;
      const rec = ioInherit.get(key) || { inputByTrack: new Map(), inputOrder: [], outputByTrack: new Map(), outputOrder: [] };
      for (const track of inst.tracks || []) {
        const tKey = (track.name || '').trim().toLowerCase();
        const ch = (track.direction || 'input') === 'output'
          ? (track.ioChannel || track.inputListChannel || 0)
          : (track.inputListChannel || 0);
        if (!ch) continue;
        const byTrack = (track.direction || 'input') === 'output' ? rec.outputByTrack : rec.inputByTrack;
        const order = (track.direction || 'input') === 'output' ? rec.outputOrder : rec.inputOrder;
        if (tKey && !byTrack.has(tKey)) byTrack.set(tKey, ch);
        if (!order.includes(ch)) order.push(ch);
      }
      ioInherit.set(key, rec);
    }
  }
  const inheritChannel = (instName: string, trackName: string, direction: 'input' | 'output', positionalIdx: number): number => {
    const rec = ioInherit.get((instName || '').trim().toLowerCase());
    if (!rec) return 0;
    const byTrack = direction === 'output' ? rec.outputByTrack : rec.inputByTrack;
    const order = direction === 'output' ? rec.outputOrder : rec.inputOrder;
    const tKey = (trackName || '').trim().toLowerCase();
    if (tKey && byTrack.has(tKey)) return byTrack.get(tKey) || 0;
    return order[positionalIdx] || order[0] || 0;
  };

  const allTracks: { ch: number; name: string; rack: string; mic: string; conn: string; tx: string; stand: string; provided: string; notes: string; standVenue: boolean; micVenue: boolean; noBoom: boolean; patchMark?: string }[] = [];
  const emptyRow = (): typeof allTracks[number] => ({ ch: 0, name: '', rack: '', mic: '', conn: '', tx: '', stand: '', provided: '', notes: '', standVenue: false, micVenue: false, noBoom: false });
  let maxChannel = 0;
  for (const m of selectedPeople) {
    for (const inst of m.instruments || []) {   // crew cards may lack the key (legacy/partial data)
      let trackIdx = 0;
      for (const track of inst.tracks || []) {
        trackIdx++;
        if ((track.direction || 'input') === 'output') continue; // outputs → Output List
        const txInfo = track.connectionType === 'wireless'
          ? [
              track.transmitterType === 'handheld' ? t('pdf.tx.handheld') :
              track.transmitterType === 'beltpack_clip' ? t('pdf.tx.beltpackClip') :
              track.transmitterType === 'beltpack_jack' ? t('pdf.tx.beltpackJack') :
              track.transmitterType === 'headset' ? t('pdf.tx.headset') : t('pdf.tx.other'),
              track.transmitterNotes || '',
            ].filter(Boolean).join(' ')
          : '';
        // Le préfixe de boom ne va plus dans la colonne STAND (il allongeait la
        // cellule et cassait la ligne) : il rejoint la colonne NOTES (user 2026-09-19).
        // ⚠️ Un clip n'est PAS un pied : il ne s'affiche pas en colonne STAND et
        // ne réclame jamais de boom (isRealMicStand, 2026-10-07).
        const standInfo = isRealMicStand(track.stand) ? STAND_LABELS[track.stand] : '';
        const noBoomNote = (isRealMicStand(track.stand) && track.withoutBoom) ? t('pdf.misc.noBoom') : '';
        const providedInfo = track.providedBy === 'venue' ? t('pdf.misc.venue') : t('pdf.misc.band');
        // Héritage remplaçant : canal explicite > hérité du titulaire (même nom
        // d'instrument) > 0 (ligne non numérotée, comme avant).
        const ch = track.inputListChannel || inheritChannel(inst.name || '', track.name || '', 'input', trackIdx - 1) || 0;
        if (ch > maxChannel) maxChannel = ch;
        allTracks.push({
          ch,
          name: track.name || '',
          rack: rackLabel(track.rackId),
          mic: track.micType || '',
          conn: track.connectionType === 'wireless' ? t('pdf.misc.wireless') : t('pdf.misc.wired'),
          tx: txInfo,
          stand: standInfo,
          provided: providedInfo,
          notes: [isProviderOnlyNote(track.notes) ? '' : (track.notes || ''), noBoomNote].filter(Boolean).join(' · '),
          // VENUE HIGHLIGHT (2026-10-18): the STAND and the MICROPHONE have
          // SEPARATE providers on a track (`providedBy` = the stand,
          // `micProvidedBy` = the microphone) — the highlight is per CELL, so
          // "wants the stand but brings the mic" tints the Stand cell only.
          standVenue: track.providedBy === 'venue',
          micVenue: (track.micProvidedBy || 'band') === 'venue',
          noBoom: !!noBoomNote, // (no boom) lives in NOTES but belongs to the STAND
          // INPUT-LIST PATCH MARK (2026-10-23, user): a per-track colour that
          // tints the Ch cell (e.g. "patch on the house stagebox").
          patchMark: (track as any).patchMark || undefined,
        });
      }
    }
  }
  // Sort by channel, then emit ONE row per channel slot from 1 to the LAST
  // channel that has a track (no extra rows after it — user rule 2026-08-07).
  // A channel BETWEEN the first and the last filled one with no track keeps an
  // EMPTY row AT ITS POSITION (the "holes" rule, e.g. channel 20 for
  // Kadebostany): the padding rows are never collected at the bottom of the
  // table. Two tracks on the same channel both render, in track order.
  allTracks.sort((a, b) => a.ch - b.ch);
  const tracksByChannel = new Map<number, typeof allTracks>();
  const unnumberedTracks: typeof allTracks = [];
  for (const t of allTracks) {
    if (t.ch > 0) {
      const list = tracksByChannel.get(t.ch) || [];
      list.push(t);
      tracksByChannel.set(t.ch, list);
    } else {
      unnumberedTracks.push(t);
    }
  }
  const paddedTracks: typeof allTracks = [...unnumberedTracks];
  // INPUT LIST (2026-10-18, user): the historical "holes" rule ALWAYS applies —
  // empty numbered rows up to the highest used channel. The per-rack / section
  // switches are for the OUTPUT list only.
  for (let ch = 1; ch <= maxChannel; ch++) {
    const filled = tracksByChannel.get(ch);
    if (filled && filled.length) paddedTracks.push(...filled);
    // Canal LIBRE : la ligne reste vide mais GARDE son numéro de canal
    // (user 2026-09-19 : la salle doit pouvoir lire les numéros des canaux libres).
    else paddedTracks.push({ ...emptyRow(), ch });
  }
  paddedTracks.forEach((t, i) => {
    // Light zebra on every other data row (row 0 of the body is the header).
    const zebra = i % 2 === 1;
    // PER-CELL highlight (2026-10-18, user): the Stand cell follows the stand's
    // provider, the Mic cell the microphone's — never the whole row.
    const cellHi = (colId: string): string | undefined => {
      // PATCH MARK (2026-10-23, user): the track's chosen colour tints the Ch
      // cell — it WINS over the venue highlight (a deliberate marking beats an
      // automatic one). Only colours, never zebra interference (highlight
      // already replaces the zebra fill in dataCell).
      if (colId === 'input-list.ch' && t.patchMark) return t.patchMark;
      if (!venueHi()) return undefined;
      if (colId === 'input-list.stand') return t.standVenue ? venueHi() : undefined;
      if (colId === 'input-list.mic') return t.micVenue ? venueHi() : undefined;
      // (no boom) sits in the NOTES cell but belongs to the STAND — it tints
      // with the stand's provider (user 2026-10-18).
      if (colId === 'input-list.notes') return (t.standVenue && t.noBoom) ? venueHi() : undefined;
      // TRACK cell (user 2026-10-23, retour sur la règle du 18.10): the track
      // name NO LONGER tints when only the STAND is venue-provided — highlight
      // only what the venue must PROVIDE (stands + their notes + the mic).
      // The mic (venue) still tints the track: the mic sits ON the track.
      if (colId === 'input-list.track') return t.micVenue ? venueHi() : undefined;
      return undefined;
    };
    inputListRows.push(
      inputColumns.map((c) => dataCell(c.get(t), { center: c.center, zebra, styleOverride: inputColOv.get(c.id), uppercase: secUppercase('input-list'), highlight: cellHi(c.id) })),
    );
  });

  // --- Output List (returns / IEMs / sends) ---
  const outputColumns = activeListColumns(L, 'output-list', OUTPUT_LIST_COLUMNS);
  const outputColOv = new Map(outputColumns.map((c) => [c.id, fieldOvById('output-list', c.id)]));
  const outputRows: any[] = [
    outputColumns.map((c) => headCell(colLabel(c.id), { center: c.center, styleOverride: outputColOv.get(c.id) })),
  ];
  const outputTracks: { rack: string; ch: number; name: string; type: string; notes: string; patchMark?: string }[] = [];
  // Sidefills of THIS rider's transport ONLY (fixed 2026-10-15). User: « les
  // sidefills apparaissent dans la Output List même si je les ai supprimés dans la
  // config audio specs ». Measured on SAINT STACY: they were deleted from the `van`
  // preset — the one the rider publishes — while plane/tourbus/train still held
  // them, and the old loop over the 4 modes (added 2026-09-19 to de-duplicate the
  // array SHARED by reference between presets) collected the survivors. A single
  // transport cannot list the same sidefill twice, so the de-dup is unnecessary.
  for (const sf of (transport ? config.specs?.[transport]?.sidefills : undefined) || []) {
    if (!sf) continue;
    outputTracks.push({
      rack: rackLabel(sf.rackId) || '—',
      ch: sf.channel || 0,
      name: sf.name || 'Sidefill',
      type: 'Sidefill',
      // Le canal vit dans la colonne CH — le répéter dans NOTES est inutile
      // (signalé par l'utilisateur 2026-09-20).
      notes: '',
    });
  }
  for (const m of selectedPeople) {
    const person = personName(m);
    for (const inst of m.instruments || []) {
      let outIdx = 0;
      for (const track of inst.tracks || []) {
        outIdx++;
        if ((track.direction || 'input') !== 'output') continue;
        outputTracks.push({
          rack: rackLabel(track.rackId) || '—',
          // Héritage remplaçant : ioChannel explicite > inputListChannel > hérité
          // du titulaire (même nom d'instrument) > 0.
          ch: track.ioChannel || track.inputListChannel || inheritChannel(inst.name || '', track.name || '', 'output', outIdx - 1) || 0,
          name: track.name || '',
          type: track.micType || (track.connectionType === 'wireless' ? 'Wireless' : 'Wired'),
          notes: track.notes || '',
          // PATCH MARK on OUTPUT tracks too (2026-10-23, user): same colour =
          // same destination (monitor world, etc.) — tints the Output List Ch cell.
          patchMark: (track as any).patchMark || undefined,
        });
      }
    }
    // Monitor outputs: IEMs and wedges are patched out of their racks.
    // ⚠️ Une ligne PAR CANAL (user 2026-09-19 : « afficher toutes les pistes, ex.
    // les deux canaux des IEM stereo ») : une paire stéréo sort en 2 lignes L/R,
    // chacune avec SON canal — même logique que la liste d'entrée.
    if (m.iemType && m.iemType !== 'none') {
      const ch = m.iemOutStart || 0;
      const stereoIem = m.iemType === 'stereo';
      for (const [idx, side] of (stereoIem ? ['L', 'R'] : ['']).entries()) {
        outputTracks.push({
          rack: rackLabel(m.iemRackId) || '—',
          ch: ch ? ch + idx : 0,
          name: `IEM — ${person}${side ? ' (' + side + ')' : ''}`,
          type: stereoIem ? 'Stereo IEM' : 'Mono IEM',
          notes: isMonitorEngineer(m) ? t('pdf.output.pfl') : '',
          // PATCH MARK (2026-10-23, user): the return group's colour tints the
          // Ch cell(s) of ALL its rows (L and R of a stereo pair share it).
          patchMark: (m as any).iemPatchMark || undefined,
        });
      }
    }
    if (m.wedges > 0) {
      const ch = m.wedgeOutStart || 0;
      const stereoWdg = m.wedges > 1 && !!m.wedgesStereo;
      const mode = stereoWdg ? 'stereo L/R' : 'linked';
      for (const [idx, side] of (stereoWdg ? ['L', 'R'] : ['']).entries()) {
        outputTracks.push({
          rack: rackLabel(m.wedgeRackId) || '—',
          ch: ch ? ch + idx : 0,
          name: `Wedge${m.wedges > 1 ? 's' : ''} — ${person}${side ? ' (' + side + ')' : ''}`,
          type: `${m.wedges} × ${mode}`,
          notes: isMonitorEngineer(m) ? t('pdf.output.pfl') : '',
          patchMark: (m as any).wedgePatchMark || undefined,
        });
      }
    }
    if (m.subwoofer) {
      const ch = m.subOutStart || 0;
      outputTracks.push({
        rack: rackLabel(m.subRackId) || '—',
        ch,
        name: `Sub — ${person}`,
        type: 'Subwoofer',
        // Idem sidefills : le canal est déjà dans la colonne CH.
        notes: '',
        patchMark: (m as any).subPatchMark || undefined,
      });
    }
  }
  // Sortie du mix principal vers la sono : 2 lignes (L/R) comme tout autre
  // output, pour que la salle voie la paire dans le tableau des sorties.
  if (cfgSound.paMixRackId && (cfgSound.paMixOutStart || 0) > 0) {
    const mixFrom = cfgSound.paMixOutStart as number;
    for (const [idx, side] of ['L', 'R'].entries()) {
      outputTracks.push({
        rack: rackLabel(cfgSound.paMixRackId) || '—',
        ch: mixFrom + idx,
        name: `${t('pdf.sound.paMixRow')} (${side})`,
        type: t('pdf.sound.paMixOutput'),
        notes: '',
      });
    }
  }
  outputTracks.sort((a, b) => (a.rack === b.rack ? a.ch - b.ch : a.rack.localeCompare(b.rack)));
  // SHOW EMPTY ROWS (2026-10-18, user) : même modèle que l'Input list, par rack
  // jusqu'à la capacité en SORTIES déclarée (défaut : désactivé sur la Output list).
  if (secShowEmpty('output-list') && outputTracks.length > 0) {
    const racksMerged = mergeStageRacks(config.globalRacks, config.specs as never, transport);
    const capOf = (label: string, fallback: number): number => {
      const r = racksMerged.find((rk) => (rackLabel(rk.id) || rk.name) === label);
      const cap = r ? rackCapacities(r).outputs : 0;
      // Same clamp for the OUTPUT list (padOutputsInRider=false → declared cap).
      if (!r) return fallback;
      if (r.padOutputsInRider === false && cap > 0) return Math.min(cap, fallback);
      return Math.max(cap, fallback);
    };
    const padded: typeof outputTracks = [];
    const groups = new Map<string, typeof outputTracks>();
    for (const t of outputTracks) {
      if (!groups.has(t.rack)) groups.set(t.rack, []);
      groups.get(t.rack)!.push(t);
    }
    for (const rk of [...groups.keys()]) {
      const tracks = groups.get(rk)!;
      const byCh = new Map<number, typeof outputTracks>();
      for (const t of tracks) { const l = byCh.get(t.ch) || []; l.push(t); byCh.set(t.ch, l); }
      const numbered = tracks.filter((t) => t.ch > 0);
      const cap = capOf(rk, numbered.length ? Math.max(...numbered.map((t) => t.ch)) : 0);
      padded.push(...tracks.filter((t) => t.ch === 0)); // non numérotées d'abord
      for (let ch = 1; ch <= cap; ch++) {
        const filled = byCh.get(ch);
        if (filled && filled.length) padded.push(...filled);
        else padded.push({ rack: rk, ch, name: '', type: '', notes: '' });
      }
      // Tracks patched beyond the displayed range still print (real data).
      padded.push(...tracks.filter((t) => t.ch > cap).sort((a, b) => a.ch - b.ch));
    }
    outputTracks.length = 0;
    outputTracks.push(...padded);
  }
  outputTracks.forEach((t, i) => {
    const zebra = i % 2 === 1;
    outputRows.push(
      // PATCH MARK (2026-10-23, user): the track's colour tints the Ch cell of
      // the OUTPUT list as well (wins over the zebra, same rule as input).
      outputColumns.map((c) => dataCell(c.get(t), { center: c.center, zebra, styleOverride: outputColOv.get(c.id), uppercase: secUppercase('output-list'), highlight: (c.id === 'output-list.ch' && t.patchMark) ? t.patchMark : undefined })),
    );
  });

  // --- Mic Stands synthesis (aggregated from tracks) ---
  // Périmètre (v0.593, option A validée user) : TOUT LE MONDE sélectionné
  // (musiciens + crew), et on ignore les pistes de SORTIE (un send FX ne réclame
  // pas de pied). La TABLE ne liste que les pieds fournis par la SALLE (le groupe
  // apporte les siens) ; les COMPTEURS, eux, distinguent les deux fournisseurs.
  const standMap = new Map<string, { label: string; qty: number; withoutBoom: boolean; providedBy: ProvidedBy; destinations: string[] }>();
  let standQtyBand = 0;   // nombre RÉEL de pieds apportés par le groupe
  let standQtyVenue = 0;  // nombre RÉEL de pieds à fournir par la salle
  for (const m of selectedPeople) {
    for (const inst of m.instruments || []) {
      for (const track of inst.tracks || []) {
        if ((track.direction || 'input') === 'output') continue;
        if (!isRealMicStand(track.stand)) continue;
        if (track.providedBy === 'venue') {
          standQtyVenue += 1;
          const key = `${track.stand}_${track.withoutBoom ? 'nobo' : 'boom'}`;
          // Destination (v0.651) — the CARRIER of the stand, lost by the old
          // type+boom aggregation. Derived from the person's name, unless the
          // track carries an explicit free-text override (`track.destination`)
          // which then WINS. Collected per (type, boom) and de-duplicated in
          // encounter order — selectedPeople is musicians (main→session→guest
          // then order) followed by crew, so the joined list is DETERMINISTIC
          // (e.g. "Sandro, Kristina").
          const dest = (track.destination || '').trim() || personName(m);
          const existing = standMap.get(key);
          if (existing) {
            existing.qty += 1;
            if (dest && !existing.destinations.includes(dest)) existing.destinations.push(dest);
          } else {
            standMap.set(key, { label: STAND_LABELS[track.stand], qty: 1, withoutBoom: !!track.withoutBoom, providedBy: 'venue', destinations: dest ? [dest] : [] });
          }
        } else {
          standQtyBand += 1;
        }
      }
    }
  }

  // --- Microphones provided by (Band vs Venue) ---
  // Périmètre identique (musiciens + crew). ⚠️ On ne compte QUE les ENTRÉES : une
  // piste de sortie (send FX, modular feed…) était comptée comme « microphone
  // provided by the band », ce qui gonflait le chiffre imprimé.
  let micProvidedByBand = 0;
  let micProvidedByVenue = 0;
  for (const m of selectedPeople) {
    for (const inst of m.instruments || []) {
      for (const track of inst.tracks || []) {
        if ((track.direction || 'input') === 'output') continue;
        if (track.micProvidedBy === 'venue') micProvidedByVenue += 1;
        else micProvidedByBand += 1;
      }
    }
  }

  // Input List is pushed later, grouped under "STAGEPLAN / INPUT-OUTPUT LIST".

  // --- Backline synthesis (aggregated from musicians AND crew) ---
  // Each item's EFFECTIVE provider = per-show override (requirements.
  // backlineProviders) > plane/train transport > the card default (band).
  // Items are keyed by type+label+provider so a venue-provided and a
  // band-provided item with the same label stay distinct.
  const backlineMap = new Map<string, BacklineRow>();
  // Items come either from the per-person cards (new shape) or, for older
  // bands, from the LEGACY shape `{ type, provided, model }` with NO
  // `quantity` / `notes` / `label`. Normalise every item so a missing
  // quantity falls back to 1 (never `undefined` → cumulative `NaN`) and a
  // legacy `model` is carried through to the PDF instead of being dropped.
  const collectBackline = (items: Array<{ id?: string; type?: string; label?: string; quantity?: number; notes?: string; imageUrl?: string; providedBy?: ProvidedBy; model?: string }>) => {
    for (const item of items) {
      // Wedges/subs print in the MONITORS section and mic stands in MIC STANDS
      // (v0.779 / v0.648) — keeping them here duplicated them in the Backline
      // table (user 2026-10-18, measured on Kadebostany).
      if (excludedFromBackline(item.type, item.label)) continue;
      const providedBy = resolveBacklineProvider(item, transport, backlineOverrideFor(req, item.id || ''));
      const rawLabel = (item.label || '').trim();
      const type = item.type || 'other';
      // The Description column shows the item's OWN label ONLY (user 2026-10-15:
      // « tu peux retirer les descriptions qui sont une copie du type »). The Type
      // column always names the item now, so falling back to the type here printed
      // it TWICE — measured on SAINT STACY: 5 card items with an empty label, which
      // is exactly what put « Drum stool / Drum stool » in the printed rider.
      // A row with neither a type nor a label (pathological) keeps a placeholder.
      const label = rawLabel || (item.type ? '' : 'Item');
      // Keep two legacy items with the same type but DIFFERENT models apart
      // (e.g. two guitar amps: Fender Twin vs Vox AC30) — merging them would
      // drop one model from the rider.
      const key = `${type}_${label}_${item.model || ''}_${providedBy}`;
      const existing = backlineMap.get(key);
      const qty = item.quantity || 1;
      if (existing) {
        existing.qty += qty;
        if (!existing.model && item.model) existing.model = item.model;
      } else {
        // Keep the RAW type in the aggregate: the Type column is a first-class
        // column and it is printed for EVERY row (2026-10-15 — it used to be
        // blanked on label-less items, which read as a broken column).
        backlineMap.set(key, { label, type, model: item.model || '', qty, notes: item.notes || '', imageUrl: item.imageUrl, providedBy });
      }
    }
  };
  for (const m of selected) collectBackline(m.backline || []);
  for (const c of selectedCrew) collectBackline(c.backline || []);
  // Band-level ADDITIONAL backline items (the "Items List" on the Audio &
  // Backline config page) — shared gear on top of the per-person cards.
  const cfgBacklineItems = (transport ? config.backline?.[transport as TransportType]?.items : undefined) || [];
  for (const it of cfgBacklineItems) {
    collectBackline([{ id: (it as any).id, type: (it as any).type || 'other', label: (it as any).label || (it as any).name || undefined, model: (it as any).model, quantity: it.quantity || 1, notes: it.notes || (it as any).details || '', providedBy: (it as any).providedBy }]);
  }

  // Backline + Mic Stands + Microphones are pushed later (page 2, after
  // Sound/IEM) so page 1 = General + Hospitality only. Build the rows here.
  let BLOCK_backlineRows: any[] | null = null;
  let BLOCK_backlineWidths: (number | string)[] = [];
  let BLOCK_standRows: any[] | null = null;
  let BLOCK_standWidths: (number | string)[] = [];
  let BLOCK_micProvided: string | null = null;

  // Backline table built MIRROR-FIRST from the ACTIVE columns of the layout
  // (v0.641): header + every row + widths all derive from the same column list,
  // so hiding/reordering a column moves its header, its cells AND its width
  // together (the classic "column header and widths out of sync" trap). The
  // table only prints when there is ≥1 active column AND at least one row.
  if (backlineMap.size > 0) {
    const backlineColumns = activeListColumns(L, 'backline', BACKLINE_COLUMNS);
    if (backlineColumns.length > 0) {
      const blRows: any[] = [
        backlineColumns.map((c) => headCell(colLabel(c.id), { center: c.center })),
      ];
      let blRowIndex = 0;
      const blHi = venueHi(); // undefined when the option is off
      for (const [, val] of backlineMap) {
        const zebra = blRowIndex++ % 2 === 1;
        // VENUE HIGHLIGHT (2026-10-18): the whole ROW of a venue-provided item.
        const rowHi = blHi && val.providedBy === 'venue' ? blHi : undefined;
        blRows.push(backlineColumns.map((c) => {
          // The Notes cell is COMPOUND (an optional reference image + the note
          // text), so it is built by hand instead of via the plain `get`.
          if (c.id === 'backline.notes') {
            const notesCell: any[] = [];
            if (val.imageUrl) notesCell.push({ image: val.imageUrl, width: 40, height: 40, alignment: 'left', margin: [0, 2, 0, 4] });
            if (val.notes) notesCell.push({ text: val.notes, margin: [0, 4, 0, 4] });
            // A COMPOUND cell (image + text) must be wrapped in a cell OBJECT so
            // it can carry the zebra fill like every other cell: a bare node
            // ARRAY has no `fillColor`, so the alternating background used to
            // STOP before this last column (bug user 2026-10-07).
            return notesCell.length
              ? { stack: notesCell, margin: CELL_MARGIN, ...(rowHi ? { fillColor: rowHi } : zebra ? { fillColor: ZEBRA_FILL } : {}) }
              : dataCell('', { center: c.center, zebra, uppercase: secUppercase('backline'), highlight: rowHi });
          }
          return dataCell(c.get(val), { center: c.center, zebra, uppercase: secUppercase('backline'), highlight: rowHi });
        }));
      }
      BLOCK_backlineRows = blRows;
      BLOCK_backlineWidths = backlineColumns.map((c) => c.width);
    }
  }

  // --- Mic Stands summary ---
  // Table built MIRROR-FIRST from the ACTIVE columns of the layout (v0.648):
  // header + every row + widths all derive from the same column list, so
  // hiding/reordering/resizing a column moves its header, its cells AND its
  // width together (same model as Backline v0.641). The table only prints when
  // there is ≥1 active column AND at least one row. The rows themselves are
  // still filtered upstream: only real stands (never a `clip`) provided by the
  // VENUE reach `standMap`.
  if (standMap.size > 0) {
    const standColumns = activeListColumns(L, 'mic-stands', MIC_STANDS_COLUMNS);
    if (standColumns.length > 0) {
      const standRows: any[] = [
        standColumns.map((c) => headCell(colLabel(c.id), { center: c.center })),
      ];
      let standRowIndex = 0;
      const standHi = venueHi(); // every standMap row is venue-provided by design
      for (const [, val] of standMap) {
        const zebra = standRowIndex++ % 2 === 1;
        const row: MicStandRow = { ...val, destination: val.destinations.join(', ') };
        standRows.push(standColumns.map((c) => dataCell(c.get(row), { center: c.center, zebra, uppercase: secUppercase('mic-stands'), highlight: standHi })));
      }
      BLOCK_standRows = standRows;
      BLOCK_standWidths = standColumns.map((c) => c.width);
    }
  }

  // --- Microphones provided by (Band vs Venue) ---
  if (standQtyBand > 0 || standQtyVenue > 0 || micProvidedByBand > 0 || micProvidedByVenue > 0) {
    const parts: string[] = [];
    if (standQtyBand > 0) parts.push(t('pdf.micProvided.standsBand', { count: standQtyBand }));
    if (standQtyVenue > 0) parts.push(t('pdf.micProvided.standsVenue', { count: standQtyVenue }));
    if (micProvidedByBand > 0) parts.push(t('pdf.micProvided.micsBand', { count: micProvidedByBand }));
    if (micProvidedByVenue > 0) parts.push(t('pdf.micProvided.micsVenue', { count: micProvidedByVenue }));
    BLOCK_micProvided = parts.join(' · ');
  }

  // === 2. HOSPITALITY ===
  // Towels & water are now per-PERSON (set on the musician/crew cards): the
  // show rider totals follow the SELECTED people, not a fixed config number.
  // Defaults (2026-10-06): musicians get 1 stage towel + 1 dressing-room towel;
  // crew get 1 dressing-room towel. Only UNDEFINED fields fall back to the
  // default (a card explicitly set to 0 stays 0). The `??` on towels:0 only
  // fires when the key is absent, not when it's 0.
  const stageTowels = selected.reduce((sum, m) => sum + (m.towels ?? 1), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.towels ?? 0) * (c.count || 1), 0);
  const dressingRoomTowels = selected.reduce((sum, m) => sum + (m.dressingRoomTowels ?? 1), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.dressingRoomTowels ?? 1) * (c.count || 1), 0);
  const stageWater = selected.reduce((sum, m) => sum + (m.water || 0), 0)
    + selectedCrew.reduce((sum, c) => sum + (c.water || 0) * (c.count || 1), 0);
  // Each hospitality bullet is tagged with its field id (same "place each
  // field as you want" mechanism as general-info). Unknown/absent field lists
  // render everything (back-compat).
  const hBulletsMap = new Map<string, any[]>();
  const hPush = (fieldId: string, node: any) => {
    const arr = hBulletsMap.get(fieldId) || [];
    arr.push(node);
    hBulletsMap.set(fieldId, arr);
  };
  const pushBullet = (fieldId: string, label: string | null, text: string) => {
    if (!fieldEnabled('hospitality', fieldId)) return;
    // Labels render UPPERCASE + bold (render-time only; the i18n strings still
    // feed the web UI in their normal/mixed case). The explicit-label branches
    // (STAGE TOWELS, DRESSING ROOMS…) already carry UPPERCASE i18n values, but
    // `.toUpperCase()` keeps the look uniform whatever a locale holds.
    // Same new-line convention as hLabel: a value starting with an empty line
    // renders the label on its own line and the value below.
    if (label && /^\s*\n/.test(text)) {
      hPush(fieldId, {
        stack: [
          { text: `${label.toUpperCase()}: `, bold: true, margin: [0, 0, 0, 1] },
          { text: text.replace(/^\s*\n+/, ''), margin: [0, 0, 0, 0] },
        ],
        margin: M4,
      });
      return;
    }
    // When the caller passes NO separate label, the text is itself a
    // "Label: value" phrase (e.g. pdf.hosp.meals = "Meals needed: 6"): split it
    // on the FIRST ':' at render so "MEALS NEEDED:" is bold + uppercase and the
    // value keeps its normal case. Never touch the i18n JSON.
    let nodeText: any;
    if (label) {
      nodeText = [{ text: `${label.toUpperCase()}: `, bold: true }, text];
    } else {
      const idx = text.indexOf(':');
      if (idx !== -1) {
        nodeText = [
          { text: `${text.slice(0, idx + 1).trim().toUpperCase()} `, bold: true },
          text.slice(idx + 1).trim(),
        ];
      } else {
        nodeText = text;
      }
    }
    hPush(fieldId, {
      text: nodeText,
      margin: M4,
    });
  };
  pushBullet('hospitality.meals', null, t('pdf.hosp.meals', { count: totalPeople }));
  // Auto-synthesized dietary restrictions from musicians + crew. The rider
  // cards no longer carry `diet` (identity moved to Team), so resolve from the
  // team diet map by shared id, falling back to any legacy card value.
  // Diets + non-food allergies sit right after "Meals needed" (2026-08-09).
  // FOOD ALLERGIES is a titled block: title bullet + one line per diet, with
  // dietNotes appended when the person has some (e.g. Arthur SS). (2026-08-09)
  const dietMap = new Map<string, { names: string[]; notes: Record<string, string> }>();
  const dietLabels: Record<string, string> = { vegetarian: t('pdf.diet.vegetarian'), vegan: t('pdf.diet.vegan'), gluten_free: t('pdf.diet.gluten_free'), lactose_free: t('pdf.diet.lactose_free'), nut_allergy: t('pdf.diet.nut_allergy'), other: t('pdf.diet.other') };
  const resolveDiet = (id: string, cardDiet?: string): string | undefined => {
    const t = dietById?.[id];
    if (t && t.diet) return t.diet;
    if (cardDiet && cardDiet !== '') return cardDiet;
    return undefined;
  };
  const resolveDietNotes = (id: string, cardNotes?: string): string | undefined => {
    const t = dietById?.[id];
    if (t && t.dietNotes) return t.dietNotes;
    if (cardNotes && cardNotes !== '') return cardNotes;
    return undefined;
  };
  const addDiet = (id: string, name: string, cardDiet?: string, cardNotes?: string) => {
    const diet = resolveDiet(id, cardDiet);
    if (!diet || diet === 'none') return;
    const label = dietLabels[diet] || diet;
    const entry = dietMap.get(label) || { names: [], notes: {} };
    entry.names.push(name);
    const notes = resolveDietNotes(id, cardNotes);
    if (notes) entry.notes[name] = notes;
    dietMap.set(label, entry);
  };
  for (const m of selected) addDiet(m.id, m.name, m.diet, m.dietNotes);
  for (const c of selectedCrew) addDiet(c.id, c.name || c.role, c.diet, c.dietNotes);
  if (dietMap.size > 0 && fieldEnabled('hospitality', 'hospitality.food-allergies')) {
    const dietLines: any[] = [];
    for (const [label, { names, notes }] of dietMap) {
      const nameText = names.map(n => (notes[n] ? `${n} (${notes[n]})` : n)).join(', ');
      dietLines.push({ text: t('pdf.diet.line', { count: names.length, label, names: nameText }), margin: [10, 1, 0, 1] });
    }
    hPush('hospitality.food-allergies', {
      stack: [
        { text: t('pdf.hosp.foodAllergies'), bold: true, margin: [0, 0, 0, 2] },
        ...dietLines,
      ],
      margin: M4,
    });
  }
  // Non-food allergies (cats/dogs, latex, dust…) — free text from the Hospitality
  // config, echoed verbatim on its own line under the titled bullet. (2026-08-09)
  // Renamed label: this is the BAND-LEVEL text field (was confusingly shown as
  // "non-food allergies"); the per-person version is `hospitality.non-food-allergies-person`. (2026-08-26)
  if (cfgHospitality.nonFoodAllergies && fieldEnabled('hospitality', 'hospitality.non-food-allergies')) {
    hPush('hospitality.non-food-allergies', {
      stack: [
        { text: t('pdf.hosp.allergiesDetails'), bold: true, margin: [0, 0, 0, 2] },
        { text: cfgHospitality.nonFoodAllergies, margin: [10, 0, 0, 0] },
      ],
      margin: M4,
    });
  }
  // Per-person non-food allergies (from Team identity, fallback legacy card
  // value) — mirrors the Food allergies block above but keyed per person. A new
  // layout field distinct from the band-level `hospitality.non-food-allergies`
  // text field (2026-08-26).
  const resolveNonFood = (id: string, cardVal?: string): string | undefined => {
    const tm = dietById?.[id];
    if (tm && tm.nonFoodAllergies && tm.nonFoodAllergies !== 'none') return tm.nonFoodAllergies;
    if (cardVal && cardVal !== '' && cardVal !== 'none') return cardVal;
    return undefined;
  };
  const nonFoodPeople: { name: string; allergy: string }[] = [];
  for (const m of selected) {
    const a = resolveNonFood(m.id, m.nonFoodAllergies);
    if (a) nonFoodPeople.push({ name: m.name, allergy: a });
  }
  for (const c of selectedCrew) {
    const a = resolveNonFood(c.id, c.nonFoodAllergies);
    if (a) nonFoodPeople.push({ name: c.name || c.role, allergy: a });
  }
  if (nonFoodPeople.length > 0 && fieldEnabled('hospitality', 'hospitality.non-food-allergies-person')) {
    hPush('hospitality.non-food-allergies-person', {
      stack: [
        { text: t('pdf.hosp.nonFoodAllergies'), bold: true, margin: [0, 0, 0, 2] },
        ...nonFoodPeople.map((p) => ({
          text: t('pdf.diet.personLine', { name: p.name, allergy: p.allergy }),
          margin: [10, 1, 0, 1] as [number, number, number, number],
        })),
      ],
      margin: M4,
    });
  }
  if (stageTowels > 0) pushBullet('hospitality.stage-towels', t('pdf.hosp.stageTowels'), String(stageTowels));
  if (dressingRoomTowels > 0) pushBullet('hospitality.dressing-towels', t('pdf.hosp.dressingTowels'), String(dressingRoomTowels));
  if (stageWater > 0) pushBullet('hospitality.side-water', t('pdf.hosp.sideWater'), t('pdf.hosp.waterBottles', { count: stageWater }));
  if (cfgHospitality.dressingRooms) pushBullet('hospitality.dressing-rooms', t('pdf.hosp.dressingRooms'), cfgHospitality.dressingRooms);
  // Miscellaneous free-text note — sits just above SECURITY when non-empty. (2026-08-09)
  if (cfgHospitality.various) pushBullet('hospitality.various', t('pdf.hosp.various'), cfgHospitality.various);
  if (cfgHospitality.security) pushBullet('hospitality.security', t('pdf.hosp.security'), cfgHospitality.security);
  if (cfgHospitality.breakfast) pushBullet('hospitality.breakfast', t('pdf.hosp.breakfast'), cfgHospitality.breakfast);
  if (cfgHospitality.lunch) pushBullet('hospitality.lunch', t('pdf.hosp.lunch'), cfgHospitality.lunch);
  if (cfgHospitality.dinner) pushBullet('hospitality.dinner', t('pdf.hosp.dinner'), cfgHospitality.dinner);
  // The free-text `catering` / `drinks` lists (one item per line, edited on the
  // Hospitality config page) ARE echoed: the user wants them in the rider PDF
  // (2026-09-16, supersedes the 2026-08-07 "intentionally NOT echoed" note).
  // They close the section, after DINNER, matching their position in the layout
  // field list. Empty lists (or fully-blank lines) emit nothing at all — no
  // label-only bullet. `pushBullet` handles the layout enable/disable check.
  const cateringText = cleanLines(cfgHospitality.catering).join('\n');
  if (cateringText) pushBullet('hospitality.catering', t('pdf.hosp.catering'), cateringText);
  const drinksText = cleanLines(cfgHospitality.drinks).join('\n');
  if (drinksText) pushBullet('hospitality.drinks', t('pdf.hosp.drinks'), drinksText);
  // === 3. SOUND REQUIREMENTS (start of page 2) ===
  const providerLabel = (p?: string) => (p === 'band' ? t('pdf.provider.band') : t('pdf.provider.venue'));

  // === 4. IEM / MONITORS ===
  // Units needed = every SELECTED person who uses in-ears — musicians AND crew
  // (crew cards have their own in-ear choice). Computed through the shared
  // helper so the rider can never disagree with the IEM config page again
  // (regression 2026-09-17: rider showed 4, config showed 6 — crew in-ears
  // were ignored here).
  // `countIemUnits` EXCLUDES wired in-ears (no transmitter to provide) — the
  // wired ones are listed separately below so the promoter knows.
  const iemCount = countIemUnits(musicians, crew, riderDoc.activeMusicianIds, riderDoc.activeCrewIds);
  // The people each IEM unit is for (transmitter users), in document order —
  // shown under "Units needed" instead of a generic "based on selected people".
  const iemPeopleNames = selectedTransmitterPeople(musicians, crew, riderDoc.activeMusicianIds, riderDoc.activeCrewIds)
    .map((p) => p.name || p.role || '')
    .filter(Boolean)
    .join(', ');
  // WHO provides each WIRED pack (2026-10-17, user : « le IEM de Chopiou est fourni
  // par le groupe et ça ne se reflète pas dans le rider ») — per person, default
  // 'band' (the feed comes from the band's monitor rack). wiredIemNames est
  // conservé pour les autres usages éventuels ; la ligne PDF porte les détails.
  const wiredIemDetails = selectedWiredPeople(musicians, crew, riderDoc.activeMusicianIds, riderDoc.activeCrewIds)
    .map((p) => {
      const name = p.name || p.role || '?';
      const prov = (p as { iemWiredProvidedBy?: ProvidedBy }).iemWiredProvidedBy || 'band';
      return `${name} (${prov === 'venue' ? t('pdf.misc.venue').toLowerCase() : t('pdf.misc.band').toLowerCase()})`;
    })
    .join(', ');

  // === 5. AUDIO & BACKLINE (Input List + Backline + Specs) ===

  // Wireless transmitters: instruments of the SELECTED musicians/crew whose
  // CARD says they use a wireless transmitter (instrument.wireless.enabled).
  // SINGLE-FLAG MODEL (2026-09-29, user decision A): the card flag is the ONLY
  // switch. The legacy per-transport specs.wirelessTransmitters[id].enabled is
  // INERT — kept in the blobs, never read here. Type/model/providers are still
  // read PER TRANSPORT from specs.wirelessTransmitters, with the SAME defaults
  // as the Audio & Backline config page (beltpack_jack / band / band) so both
  // surfaces show the same thing. The signal itself enters wired (DI/mic) — the
  // transmitter is a SEPARATE rider request. (2026-08-12, rev. 2026-09-29)
  const specWireless = (config.specs?.[transport as 'van' | 'plane' | 'tourbus' | 'train']?.wirelessTransmitters) || {};
  const wirelessRows: any[] = [];
  // ⚠️ v0.593 : le commentaire ci-dessus disait déjà « musicians/crew » mais la boucle
  // n'itérait que `selected` → un émetteur HF porté par un CREW était silencieusement
  // absent du rider, alors que ConfigAudioBacklinePage l'agrège (musiciens ET crew).
  for (const m of selectedPeople) {
    for (const inst of m.instruments || []) {
      if (!inst?.wireless?.enabled) continue; // ← the CARD flag = single source of truth
      const w = specWireless[inst.id] || {};  // ← per-transport detail only
      const txType = w.transmitterType || 'beltpack_jack';
      const tx =
        txType === 'handheld' ? t('pdf.tx.handheld') :
        txType === 'beltpack_clip' ? t('pdf.tx.beltpackClip') :
        txType === 'beltpack_jack' ? t('pdf.tx.beltpackJack') :
        txType === 'headset' ? t('pdf.tx.headset') : t('pdf.tx.other');
      const micP = w.micProvider || 'band';
      const recP = w.receiverProvider || 'band';
      const z = wirelessRows.length % 2 === 1; // light zebra on every other row
      // VENUE HIGHLIGHT (2026-10-18): highlight the row when the mic OR the
      // receiver is requested from the venue.
      const rowHi = venueHi() && (micP === 'venue' || recP === 'venue') ? venueHi() : undefined;
      wirelessRows.push([
        dataCell(m.name || '?', { zebra: z, uppercase: secUppercase('audio-backline'), highlight: rowHi }),
        dataCell(inst.name || '—', { zebra: z, uppercase: secUppercase('audio-backline'), highlight: rowHi }),
        dataCell(tx + (w.notes ? ` (${w.notes})` : ''), { zebra: z, uppercase: secUppercase('audio-backline'), highlight: rowHi }),
        dataCell(micP === 'band' ? t('pdf.misc.band') : t('pdf.misc.venue'), { center: true, zebra: z, uppercase: secUppercase('audio-backline'), highlight: rowHi }),
        dataCell(recP === 'band' ? t('pdf.misc.band') : t('pdf.misc.venue'), { center: true, zebra: z, uppercase: secUppercase('audio-backline'), highlight: rowHi }),
      ]);
    }
  }
  // Backline + Mic Stands + Microphones (built earlier; pushed here so
  // page 1 = General + Hospitality, page 2 = tech sections). All three are
  // emitted by their layout builders below (auto-hidden when no data).

  // === 6. STAGEPLAN / INPUT-OUTPUT LIST ===
  // Emitted by the layout-driven builders below (stageplan wrapper + the
  // input-list / output-list tables; stage-plot keeps its page break).

  const versionLabel = cfgGeneral.version || 'v1.0';

  // === LAYOUT-DRIVEN EMISSION ===
  // Each section of the layout maps to one builder. Builders only EMIT
  // Content[] from the aggregates computed above — they never recompute data.
  // Returning null auto-hides a section (conditional blocks with no data),
  // even when the layout has it enabled.
  const fill = (tpl: string, vars: Record<string, string | number>): string =>
    tpl.replace(/\{(\w+)\}/g, (_, k: string) => (vars[k] !== undefined ? String(vars[k]) : ''));

  // A section chunk = the content emitted for ONE layout field (id = the field
  // id, null for an unnamed block such as a conditional table).
  type SectionChunk = { id: string | null; nodes: Content[] };
  // Re-emit a section's chunks in the LAYOUT's field order so the editor's ↑/↓
  // reorder is honoured in the PDF (sound / iem / audio-backline / lights /
  // stage-design used to emit in hard-coded code order, making the arrows a
  // no-op). Spacers and free-text fields inserted by the editor are emitted at
  // their position; chunks whose id is not in the layout keep code order and are
  // appended at the end so nothing is silently dropped.
  const orderChunks = (sectionId: PdfSectionId, chunks: SectionChunk[]): Content[] => {
    const fields = L.sections.find((s) => s.id === sectionId)?.fields || [];
    if (fields.length === 0) return chunks.flatMap((c) => c.nodes);
    const known = new Set(fields.map((f) => f.id));
    const out: Content[] = [];
    for (const f of fields) {
      const fid = f.id;
      if (!fieldEnabled(sectionId, fid)) continue;
      if (isSpacerField(fid)) {
        out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4))));
        continue;
      }
      if (isTextField(fid)) {
        const node = textNode(f);
        if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f)));
        continue;
      }
      for (const c of chunks) {
        if (c.id === fid) {
          out.push(...applyFieldStyle(c.nodes, fieldStyleOverride(f)));
        }
      }
    }
    for (const c of chunks) {
      if (c.id === null || !known.has(c.id)) out.push(...c.nodes);
    }
    return out;
  };

  const buildTitle = (): Content[] => {
    // The section's title override (if any) replaces the whole main title line;
    // {bandName} / {titleSuffix} placeholders are filled like the other texts.
    const override = secMap.get('title')?.titleOverride?.trim();
    // The band name lives in the logo — the title line is just the suffix
    // (e.g. "TECH & HOSPITALITY RIDER 2026"), unless a custom title override
    // is set for the section. BOTH go through `fill` so {bandName} (and the
    // other tokens) are substituted instead of printed literally — the "Title
    // suffix" field is advertised with the {bandName} placeholder in the
    // editor, so it must resolve the same way the running header does.
    const titleText = fill(override || L.texts.titleSuffix, {
      bandName: bandName ?? 'TECHNICAL',
      titleSuffix: L.texts.titleSuffix,
      versionDetail,
      versionLabel,
    });
    const logoNode = (bandLogo && secStyle.showLogo !== false)
      ? { image: bandLogo, width: secStyle.logoWidth ?? 180, alignment: 'center', margin: [0, 8, 0, 10] as [number, number, number, number] }
      : { text: bandName ?? 'TECHNICAL', style: { fontSize: secStyle.titleFontSize, bold: secStyle.titleBold !== false, alignment: 'center' }, margin: [0, 8, 0, 10] as [number, number, number, number] };
    const titleNode = { text: titleText, style: { fontSize: secStyle.titleFontSize, bold: secStyle.titleBold !== false, alignment: 'center', margin: [0, 0, 0, 2] as [number, number, number, number] } };
    const versionNode = { text: fill(L.texts.versionTemplate, { versionDetail }), style: { fontSize: secStyle.subtitleFontSize, bold: secStyle.subtitleBold !== false, italics: true, alignment: 'center', color: secStyle.subtitleColor }, margin: [0, 0, 0, 8] as [number, number, number, number] };
    const tourNode = (tourName && L.texts.showTourName !== false)
      ? { text: String(tourName), style: { fontSize: secStyle.subtitleFontSize, bold: secStyle.subtitleBold !== false, alignment: 'center', color: secStyle.subtitleColor }, margin: [0, 0, 0, 2] as [number, number, number, number] }
      : null;
    // LAYOUT-DRIVEN (2026-10-18, user): the editor's field list for the title
    // section (spacers / free text / enable-disable) is honoured — before, a
    // spacer inserted above the title suffix silently did nothing. Only the
    // KNOWN ids (titleSuffix / versionTemplate) are special; spacers and free
    // text render at their position. No saved field list → historical look.
    const tsec = L.sections.find((s) => s.id === 'title');
    const orderFields = tsec?.fields || [];
    if (orderFields.length === 0) {
      return [logoNode, ...(tourNode ? [tourNode] : []), titleNode, versionNode];
    }
    // The LOGO (or band name) and the tour name ALWAYS print first — they are
    // the document identity, not layout fields (fix 2026-10-18: a saved field
    // list used to drop them). The walk below only orders the title line, the
    // version template and the spacers/free text the editor adds around them.
    const out: Content[] = [logoNode, ...(tourNode ? [tourNode] : [])];
    let sawTitle = false;
    let sawVersion = false;
    for (const f of orderFields) {
      if (f.enabled === false) continue;
      const fid = f.id;
      if (fid === 'spacer' || fid.endsWith('.spacer')) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
      if (fid === 'text' || fid.endsWith('.text')) { const node = textNode(f); if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f))); continue; }
      if (fid === 'titleSuffix') { out.push(titleNode); sawTitle = true; }
      else if (fid === 'versionTemplate') { out.push(versionNode); sawVersion = true; }
    }
    // A saved list missing the known ids (or an old layout) still prints the
    // title line and the version template — they are the block's data.
    if (!sawTitle) out.push(titleNode);
    if (!sawVersion) out.push(versionNode);
    return out;
  };

  const buildContact = (): Content[] | null => {
    // Per-band overrides win over the automatically-detected manager (Team,
    // manager tag) — same manager may use a band-specific email/phone/address.
    const overrideName = L.texts.contactOverrideName?.trim();
    const overrideEmail = L.texts.contactOverrideEmail?.trim();
    const overridePhone = L.texts.contactOverridePhone?.trim();
    const hasOverride = !!(overrideName || overrideEmail || overridePhone);
    const cName = overrideName || manager?.name || '';
    const cEmail = overrideEmail || manager?.email || '';
    const cPhone = overridePhone || manager?.phone || '';
    const cLine = [cName, cEmail, cPhone].filter(Boolean).join(' / ');
    if (!cLine) return null;
    const labelNode = { text: L.texts.contactLabel.toUpperCase(), bold: true, alignment: 'center', margin: [0, 0, 0, 1] as [number, number, number, number] };
    const lineNode = {
      text: cLine,
      alignment: 'center',
      margin: [0, 0, 0, 12] as [number, number, number, number],
      ...(hasOverride ? { decoration: 'underline', decorationColor: secStyle.subtitleColor } : {}),
    };
    // LAYOUT-DRIVEN (2026-10-18, user): same model as buildTitle — spacers /
    // free text placed in the contact section now render. The override fields
    // (name/email/phone) are INPUTS, not printed nodes. No saved field list →
    // historical two-line look. Order: spacers/free text FIRST (the user adds
    // them ABOVE the label), then the LABEL, then the contact LINE (block data
    // — always last). The label can never be dropped by a saved field list
    // (fix 2026-10-18).
    const csec = L.sections.find((s) => s.id === 'contact');
    const cFields = csec?.fields || [];
    if (cFields.length === 0) return [labelNode, lineNode];
    const out: Content[] = [];
    for (const f of cFields) {
      if (f.enabled === false) continue;
      const fid = f.id;
      if (fid === 'spacer' || fid.endsWith('.spacer')) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
      if (fid === 'text' || fid.endsWith('.text')) { const node = textNode(f); if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f))); continue; }
    }
    out.push(labelNode);
    out.push(lineNode);
    return out;
  };

  // Field ids ending in `.spacer` (or exactly 'spacer') emit a vertical gap
  // instead of content — the editor inserts them to space out sub-sections.
  const isSpacerField = (fid: string): boolean => fid === 'spacer' || fid.endsWith('.spacer');
  // Free-text fields (id 'text') emit a user-defined paragraph with basic
  // formatting (bold / italic / font size / alignment).
  const isTextField = (fid: string): boolean => fid === 'text' || fid.endsWith('.text');
  // pdfmake gives a `{ text: '' }` node at least one full line height (~13px),
  // so small spacers never shrink below that. An invisible canvas has an exact
  // height, so the configured size is honored down to 0.
  const spacerNode = (size: number): Content => ({
    canvas: [{ type: 'line', x1: 0, y1: 0, x2: 0, y2: 0, lineWidth: 0 }],
    margin: [0, Math.max(0, size) / 2, 0, Math.max(0, size) / 2],
  } as unknown as Content);
  const textNode = (f: { textContent?: string; bold?: boolean; italic?: boolean; fontSize?: number; align?: string }): Content | null => {
    const content = (f.textContent ?? '').trim();
    if (!content) return null;
    return {
      text: content,
      ...(f.bold ? { bold: true } : {}),
      ...(f.italic ? { italics: true } : {}),
      ...(f.fontSize ? { fontSize: f.fontSize } : {}),
      ...(f.align && f.align !== 'left' ? { alignment: f.align as 'center' | 'right' } : {}),
      margin: M,
    } as unknown as Content;
  };

  // A "block" section (wireless / backline / mic-stands / mic-provided /
  // stage-plot / light-plot) has ONE monolithic block of content (a table, a
  // text summary or an image) addressed by a single layout field id. Since
  // v0.640 these sections honour the layout field ORDER so the spacers /
  // free-text fields inserted by the Rider Layout editor render around the
  // block (they were persisted but never emitted before). Auto-hide is kept:
  // the section prints when its block has data OR at least one enabled
  // free-text field carries text — a lone spacer never prints a bare heading.
  // When the block field is missing from a saved field list it is still
  // emitted (back-compat), following the `emitted` flag pattern of buildIntro.
  //
  // v0.642: `contentById` adds NAMED content chunks (e.g. Wireless'
  // `wireless.mics` / `wireless.frequency` spec lines) that are emitted at the
  // position of their own id in the layout field order — exactly like the
  // lines of the column-based sections, but inside a block section. Each named
  // chunk prints ONCE, is skipped when its field is disabled, and still prints
  // (appended) when a saved field list lost its id (back-compat), so nothing is
  // ever silently dropped.
  const blockSectionNodes = (
    sectionId: PdfSectionId,
    blockFieldIds: string[] | string,
    blockNodes: Content[] | null,
    contentById: Record<string, Content[]> = {},
  ): Content[] | null => {
    // A section's "block" may be addressed by SEVERAL field ids (Backline, since
    // v0.641: its 6 columns form ONE logical table). The table is emitted ONCE,
    // at the position of the FIRST enabled block id encountered in the field
    // order — spacers / free-text before it print ABOVE the table, the ones
    // after print BELOW it. The remaining block ids are skipped (they belong to
    // the same table).
    const blockIds = Array.isArray(blockFieldIds) ? blockFieldIds : [blockFieldIds];
    const sec = L.sections.find((s) => s.id === sectionId);
    const fields = sec?.fields || [];
    const blockEnabled = blockIds.some((id) => fieldEnabled(sectionId, id));
    const nodes = blockEnabled && blockNodes && blockNodes.length > 0 ? blockNodes : null;
    // Free-text fields with real content keep the section alive on their own.
    const hasFreeText = fields.some((f) => {
      if (!isTextField(f.id) || f.enabled === false) return false;
      return (f.textContent ?? '').trim().length > 0;
    });
    // Named content chunks with real content also keep the section alive.
    const hasNamedContent = Object.keys(contentById).some(
      (id) => contentById[id].length > 0 && fieldEnabled(sectionId, id),
    );
    if (!nodes && !hasFreeText && !hasNamedContent) return null; // nothing to show → auto-hide
    const out: Content[] = [];
    if (fields.length === 0) {
      // Layout saved without a field list: historical output, unchanged —
      // named chunks (insertion order) then the block.
      for (const id of Object.keys(contentById)) if (contentById[id].length) out.push(...contentById[id]);
      if (nodes) out.push(...nodes);
      return out;
    }
    let emitted = false;
    const emittedNamed = new Set<string>();
    for (const f of fields) {
      const fid = f.id;
      if (!fieldEnabled(sectionId, fid)) continue;
      if (isSpacerField(fid)) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
      if (isTextField(fid)) { const node = textNode(f); if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f))); continue; }
      if (blockIds.includes(fid)) {
        // Emit the block ONCE, at the first enabled block id. The remaining
        // block ids (the other columns of the same table) emit nothing.
        if (nodes && !emitted) {
          out.push(...applyFieldStyle(nodes, fieldStyleOverride(f)));
          emitted = true;
        }
        continue;
      }
      if (contentById[fid] && !emittedNamed.has(fid)) {
        out.push(...applyFieldStyle(contentById[fid], fieldStyleOverride(f)));
        emittedNamed.add(fid);
        continue;
      }
    }
    // A saved field list that lost the block field must never drop the block.
    if (!emitted && nodes) out.push(...applyFieldStyle(nodes, fieldOvById(sectionId, blockIds[0])));
    // Same back-compat for the named chunks whose id is missing from the list.
    for (const id of Object.keys(contentById)) {
      if (!emittedNamed.has(id) && contentById[id].length && fieldEnabled(sectionId, id)) {
        out.push(...applyFieldStyle(contentById[id], fieldOvById(sectionId, id)));
      }
    }
    return out;
  };

  // === LEGAL INTRO (above GENERAL INFORMATION, Rider-Intro-Section-Spec
  // 2026-09-16) ===
  // The legal intro sentence, italic, at the very top of the rider — emitted
  // as its OWN section so the Rider Layout editor can toggle/reorder it
  // independently. No heading (identical to the historical look: just the
  // italic sentence, then GENERAL INFORMATION below).
  const buildIntro = (): Content[] => {
    const out: Content[] = [];
    // The section's OWN content: the italic legal sentence.
    const introNodes: Content[] = [
      { text: L.texts.introText, italics: true, margin: [0, 0, 0, 6] as [number, number, number, number] },
    ];
    const sec = L.sections.find((s) => s.id === 'intro');
    const orderFields = sec?.fields || [];
    if (orderFields.length === 0) {
      // Layout saved without a field list: historical output, unchanged.
      if (fieldEnabled('intro', 'intro.intro')) {
        out.push(...applyFieldStyle(introNodes, fieldOvById('intro', 'intro.intro')));
      }
      return out;
    }
    // The editor lets the user reorder this section's fields and insert
    // SPACERS / free-text blocks into it, so the emission must follow the
    // LAYOUT field order like the other builders do — otherwise a spacer added
    // to the intro section is persisted but never reaches the PDF.
    let emitted = false;
    for (const f of orderFields) {
      const fid = f.id;
      if (!fieldEnabled('intro', fid)) continue;
      if (isSpacerField(fid)) {
        out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4))));
        continue;
      }
      if (isTextField(fid)) {
        const node = textNode(f);
        if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f)));
        continue;
      }
      if (fid === 'intro.intro') {
        out.push(...applyFieldStyle(introNodes, fieldStyleOverride(f)));
        emitted = true;
      }
    }
    // A saved field list that lost the sentence must never drop it.
    if (!emitted && fieldEnabled('intro', 'intro.intro')) {
      out.push(...applyFieldStyle(introNodes, fieldOvById('intro', 'intro.intro')));
    }
    return out;
  };

  /**
   * TECHNICAL RIDER — own section ABOVE FOH & MON (v0.591). Holds the technical
   * intro that used to sit at the top of the Sound section.
   * The text is a band-level LAYOUT text (texts.techIntroText, like the legal
   * intro) with a read-FALLBACK on the legacy SoundConfig.intro, so bands that
   * never re-edit it keep printing their current text.
   * Emission follows the layout field order → spacers / free text work here too.
   */
  const buildTechnicalRider = (): Content[] | null => {
    const out: Content[] = [];
    // Fallback chain: the band's LAYOUT text (key present, even empty = the user
    // cleared it on purpose) → else the legacy SoundConfig.intro (bands that
    // never re-edited it keep their customised wording) → else auto-hide.
    const layoutText = L.texts.techIntroText;
    const text = (layoutText === undefined ? cfgSound.intro || '' : layoutText).trim();
    if (!text) return null; // no technical intro → section auto-hides (never a bare heading)
    const introNodes: Content[] = [
      { text, italics: true, margin: [0, 0, 0, 6] as [number, number, number, number] },
    ];
    // VENUE HIGHLIGHT note (2026-10-18, user): right after the technical intro,
    // only when the highlight is on — tells the promoter what the tint means.
    // SAME font & size as the intro text (user 2026-10-18): inherits the doc
    // defaults, only the background tint marks it.
    if (venueHi()) {
      introNodes.push({
        text: t('pdf.tr.venueHighlightNote'),
        italics: true,
        background: venueHi(),
        margin: [0, 0, 0, 6] as [number, number, number, number],
      } as Content);
    }
    const tsec = L.sections.find((s) => s.id === 'technical-rider');
    const orderFields = tsec?.fields || [];
    if (orderFields.length === 0) {
      if (fieldEnabled('technical-rider', 'technical-rider.intro')) {
        out.push(...applyFieldStyle(introNodes, fieldOvById('technical-rider', 'technical-rider.intro')));
      }
      return [
        sectionTitle(secTitle('technical-rider', t('pdf.section.technical-rider')), { pageBreakBefore: secPbb('technical-rider') }),
        ...out,
      ];
    }
    let emitted = false;
    for (const f of orderFields) {
      const fid = f.id;
      if (!fieldEnabled('technical-rider', fid)) continue;
      if (isSpacerField(fid)) { out.push(spacerNode(Math.max(0, Math.min(60, Number(f.size) || 4)))); continue; }
      if (isTextField(fid)) { const node = textNode(f); if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f))); continue; }
      if (fid === 'technical-rider.intro') {
        out.push(...applyFieldStyle(introNodes, fieldStyleOverride(f)));
        emitted = true;
      }
    }
    if (!emitted && fieldEnabled('technical-rider', 'technical-rider.intro')) {
      out.push(...applyFieldStyle(introNodes, fieldOvById('technical-rider', 'technical-rider.intro')));
    }
    // Section heading FIRST (carries the "New page" flag), like every other
    // title-bearing builder. A section with no content auto-hides above.
    return [
      sectionTitle(secTitle('technical-rider', t('pdf.section.technical-rider')), { pageBreakBefore: secPbb('technical-rider') }),
      ...out,
    ];
  };

  const buildGeneralInfo = (): Content[] => {
    const out: Content[] = [];
    // The legal intro sentence now lives in its own 'intro' section above
    // General Information (Rider-Intro-Section-Spec 2026-09-16) — emitted by
    // buildIntro, not here anymore.
    out.push(sectionTitle(secTitle('general-info', t('pdf.section.general-info')), { pageBreakBefore: secPbb('general-info') }));
    // Emit field content in the LAYOUT's field order (the editor reorders
    // fields inside the section; unknown/absent field lists keep code order).
    const sec = L.sections.find((s) => s.id === 'general-info');
    const orderFields = sec?.fields || [];
    const order = orderFields.map((f) => f.id);
    if (order.length === 0) {
      for (const [fid, nodes] of generalFieldContent) {
        out.push(...applyFieldStyle(nodes, fieldOvById('general-info', fid)));
      }
    } else {
      const emitted = new Set<string>();
      for (const f of orderFields) {
        const fid = f.id;
        if (!fieldEnabled('general-info', fid)) continue;
        if (isSpacerField(fid)) {
          const h = Math.max(0, Math.min(60, Number(f.size) || 4));
          out.push(spacerNode(h));
          continue;
        }
        if (isTextField(fid)) {
          const node = textNode(f);
          if (node) out.push(...applyFieldStyle([node], fieldStyleOverride(f)));
          continue;
        }
        const nodes = generalFieldContent.get(fid);
        if (nodes) {
          out.push(...applyFieldStyle(nodes, fieldStyleOverride(f)));
          emitted.add(fid);
        }
      }
      // Fields not in the layout list but with content (e.g. legacy config
      // values) still render at the end so nothing is silently dropped.
      for (const [fid, nodes] of generalFieldContent) {
        if (!emitted.has(fid) && fieldEnabled('general-info', fid)) {
          out.push(...applyFieldStyle(nodes, fieldOvById('general-info', fid)));
        }
      }
    }
    return out;
  };

  const buildHospitality = (): Content[] => {
    const out: Content[] = [sectionTitle(secTitle('hospitality', t('pdf.section.hospitality')), { pageBreakBefore: secPbb('hospitality') })];
    const sec = L.sections.find((s) => s.id === 'hospitality');
    const orderFields = sec?.fields || [];
    const order = orderFields.map((f) => f.id);
    const bullets: any[] = [];
    if (order.length === 0) {
      // No field list → code order (back-compat).
      for (const [fid, nodes] of hBulletsMap) {
        bullets.push(...applyFieldStyle(nodes, fieldOvById('hospitality', fid)));
      }
    } else {
      const emitted = new Set<string>();
      for (const f of orderFields) {
        const fid = f.id;
        if (!fieldEnabled('hospitality', fid)) continue;
        if (isSpacerField(fid)) {
          const h = Math.max(0, Math.min(60, Number(f.size) || 4));
          bullets.push(spacerNode(h));
          continue;
        }
        if (isTextField(fid)) {
          const node = textNode(f);
          if (node) bullets.push(...applyFieldStyle([node], fieldStyleOverride(f)));
          continue;
        }
        const nodes = hBulletsMap.get(fid);
        if (nodes) {
          bullets.push(...applyFieldStyle(nodes, fieldStyleOverride(f)));
          emitted.add(fid);
        }
      }
      // Tagged content not listed in the layout still renders at the end.
      for (const [fid, nodes] of hBulletsMap) {
        if (!emitted.has(fid) && fieldEnabled('hospitality', fid)) {
          bullets.push(...applyFieldStyle(nodes, fieldOvById('hospitality', fid)));
        }
      }
    }
    out.push({ ul: bullets, marker: '●', margin: [0, 0, 0, 8] as [number, number, number, number] });
    return out;
  };

  const buildSound = (): Content[] => {
    const chunks: SectionChunk[] = [];
    const line = (fieldId: string, node: Content) => chunks.push({ id: fieldId, nodes: [node] });
    if (fieldEnabled('sound', 'sound.foh')) {
      line('sound.foh', { ...hLabel(`${t('pdf.sound.foh')}${providerLabel(cfgSound.fohProvider)}: ${cfgSound.fohConsole || 'TBD'}`), margin: M, ...(lineHi(cfgSound.fohProvider || 'venue') ? { background: lineHi(cfgSound.fohProvider || 'venue') } : {}) });
    }
    // What we need AT the FOH position (table/riser, power) — right under the desk.
    // Sortie du MIX PRINCIPAL vers la sono : un RACK + une PAIRE STÉRÉO choisie
    // dans la config (ex. Wing Rack 1-2) — user 2026-09-19. Émission
    // CONDITIONNELLE : rack ou canal absent = rien imprimé.
    if (fieldEnabled('sound', 'sound.pa-mix-output') && cfgSound.paMixRackId && (cfgSound.paMixOutStart || 0) > 0) {
      const mixFrom = cfgSound.paMixOutStart as number;
      const mixWhere = `${rackLabel(cfgSound.paMixRackId) || '—'} ${t('pdf.sound.paMixChannels', { from: mixFrom, to: mixFrom + 1 })}`;
      line('sound.pa-mix-output', { ...hLabel(`${t('pdf.sound.paMixOutput')}: ${mixWhere}`), margin: M });
    }
    if (fieldEnabled('sound', 'sound.foh-requirements') && cfgSound.fohRequirements) {
      line('sound.foh-requirements', { ...hLabel(`${t('pdf.sound.fohRequirements')}: ${cfgSound.fohRequirements}`), margin: M, ...(lineHi(cfgSound.fohRequirementsProvider || 'venue') ? { background: lineHi(cfgSound.fohRequirementsProvider || 'venue') } : {}) });
    }
    if (fieldEnabled('sound', 'sound.pa')) {
      line('sound.pa', { ...hLabel(`${t('pdf.sound.pa')}${providerLabel(cfgSound.paProvider)}: ${cfgSound.paSystem || 'TBD'}`), margin: M, ...(lineHi(cfgSound.paProvider || 'venue') ? { background: lineHi(cfgSound.paProvider || 'venue') } : {}) });
    }
    // Monitor console (the desk) sits ABOVE the monitor system: a band can tour
    // with its own desk while asking the venue for the wedges / IEM.
    if (fieldEnabled('sound', 'sound.monitor-console')) {
      line('sound.monitor-console', { ...hLabel(`${t('pdf.sound.monitorConsole')}${providerLabel(cfgSound.monitorConsoleProvider)}: ${cfgSound.monitorConsole || 'TBD'}`), margin: M, ...(lineHi(cfgSound.monitorConsoleProvider || 'venue') ? { background: lineHi(cfgSound.monitorConsoleProvider || 'venue') } : {}) });
    }
    // What we need AT the monitor position — right under the monitor console.
    if (fieldEnabled('sound', 'sound.monitor-requirements') && cfgSound.monitorRequirements) {
      line('sound.monitor-requirements', { ...hLabel(`${t('pdf.sound.monitorRequirements')}: ${cfgSound.monitorRequirements}`), margin: M, ...(lineHi(cfgSound.monitorRequirementsProvider || 'venue') ? { background: lineHi(cfgSound.monitorRequirementsProvider || 'venue') } : {}) });
    }
    if (fieldEnabled('sound', 'sound.monitor')) {
      line('sound.monitor', { ...hLabel(`${t('pdf.sound.monitor')}${providerLabel(cfgSound.monitorProvider)}: ${cfgSound.monitorSystem || 'TBD'}`), margin: M4, ...(lineHi(cfgSound.monitorProvider || 'venue') ? { background: lineHi(cfgSound.monitorProvider || 'venue') } : {}) });
    }
    // Sound limit lives in the General config but is displayed here, under
    // Sound (user pref 2026-08-09; edited on the Sound config page).
    if (cfgGeneral.soundLimitDb && fieldEnabled('sound', 'sound.limit')) {
      line('sound.limit', { ...hLabel(`${t('pdf.sound.limit')}: ${cfgGeneral.soundLimitDb}`), margin: M4 });
    }
    // Emitted in the LAYOUT's field order (editor ↑/↓).
    return [sectionTitle(secTitle('sound', t('pdf.section.sound')), { pageBreakBefore: secPbb('sound') }), ...orderChunks('sound', chunks)];
  };

  const buildIem = (): Content[] => {
    const chunks: SectionChunk[] = [];
    const iemLine = (fieldId: string, text: string, margin: any = M, hi?: string) => {
      if (fieldEnabled('iem', fieldId)) chunks.push({ id: fieldId, nodes: [{ ...hLabel(text), margin, ...(hi ? { background: hi } : {}) }] });
    };
    iemLine('iem.system', `${t('pdf.iem.system')}: ${cfgIem.system || 'TBD'}${providerLabel(cfgIem.provider)}`, M, lineHi(cfgIem.provider || 'venue'));
    iemLine('iem.units', iemPeopleNames
      ? t('pdf.iem.unitsNamed', { count: iemCount, names: iemPeopleNames })
      : t('pdf.iem.units', { count: iemCount }), M4, lineHi(cfgIem.provider || 'venue'));
    if (cfgIem.mixType) iemLine('iem.mix', `${t('pdf.iem.mix')}: ${cfgIem.mixType}`, M4);
    if (cfgIem.frequencyRange) iemLine('iem.frequency', `${t('pdf.iem.frequency')}: ${cfgIem.frequencyRange}${providerLabel(cfgIem.provider)}`, M, lineHi(cfgIem.provider || 'venue'));
    // Transmitter count is auto (selected musicians' in-ear choice); extra
    // bodypacks (spare / monitor engineer / guest) are entered manually.
    const iemTotalBodypacks = iemCount + (cfgIem.extraBodypacks || 0);
    if (iemTotalBodypacks > 0) iemLine('iem.bodypacks', t('pdf.iem.bodypacks', { count: iemTotalBodypacks }), M4, lineHi(cfgIem.provider || 'venue'));
    // Wired in-ears: they need an output from the monitor rack but NO
    // transmitter — spelling it out avoids the promoter quoting extra units.
    // Wired packs carry their per-person provider in parens (2026-10-17) —
    // same i18n key, the details string embeds « (band) » / « (venue) ».
    if (wiredIemDetails) iemLine('iem.wired', t('pdf.iem.wired', { names: wiredIemDetails }), M4);
    // WEDGES (retours) + SUBWOOFER — déplacés dans la section MONITORS séparée
    // (2026-10-17, user) : ce sont des besoins monitor, pas de l'IEM.
    if (cfgIem.notes) iemLine('iem.note', `${t('pdf.iem.note')}: ${cfgIem.notes}`, M4, lineHi(cfgIem.provider || 'venue'));
    return [sectionTitle(secTitle('iem', t('pdf.section.iem')), { pageBreakBefore: secPbb('iem') }), ...orderChunks('iem', chunks)];
  };

  // Section MONITORS (2026-10-17, user) : les wedges/retours + subwoofer — des
  // besoins monitor, SÉPARÉS des IEM. Les totaux suivent les personnes
  // sélectionnées ; le fournisseur est par transport (venue par défaut). Les
  // wedges d'un ingé MON sont pour le PFL (libellé court — PFL est connu).
  const buildMonitors = (): Content[] => {
    const chunks: SectionChunk[] = [];
    const monLine = (fieldId: string, text: string, margin: any = M, hi?: string) => {
      if (fieldEnabled('monitors', fieldId)) chunks.push({ id: fieldId, nodes: [{ ...hLabel(text), margin, ...(hi ? { background: hi } : {}) }] });
    };
    const wedgesTotal = selected.reduce((sum, m) => sum + (m.wedges || 0), 0)
      + selectedCrew.reduce((sum, c) => sum + (c.wedges || 0), 0);
    const subAny = [...selected, ...selectedCrew].some((m) => m.subwoofer);
    const wedgesPfl = [...selected, ...selectedCrew].filter((m) => (m.wedges || 0) > 0 && isMonitorEngineer(m)).reduce((sum, m) => sum + (m.wedges || 0), 0);
    if (wedgesTotal > 0) {
      const wedgesLine = t('pdf.monitors.wedges', { count: wedgesTotal, provider: (cfgIem.wedgesProvider || 'venue') === 'venue' ? t('pdf.misc.venue').toLowerCase() : t('pdf.misc.band').toLowerCase() })
        + (wedgesPfl > 0 ? ` — ${t('pdf.iem.wedgesPfl', { count: wedgesPfl })}` : '');
      monLine('monitors.wedges', wedgesLine, M, lineHi(cfgIem.wedgesProvider || 'venue'));
    }
    const subCount = [...selected, ...selectedCrew].filter((m) => m.subwoofer).length;
    // hLabel renders everything before the first ':' bold + UPPERCASE — the
    // sub line must carry a count AND a colon to match the wedges line.
    if (subAny) monLine('monitors.sub', t('pdf.monitors.sub', { count: subCount, provider: (cfgIem.subProvider || 'venue') === 'venue' ? t('pdf.misc.venue').toLowerCase() : t('pdf.misc.band').toLowerCase() }), M, lineHi(cfgIem.subProvider || 'venue'));
    if (chunks.length === 0) chunks.push({ id: null, nodes: [{ text: t('pdf.monitors.none'), margin: M4 }] });
    return [sectionTitle(secTitle('monitors', t('pdf.section.monitors')), { pageBreakBefore: secPbb('monitors') }), ...orderChunks('monitors', chunks)];
  };

  const buildAudioBackline = (): Content[] => {
    const chunks: SectionChunk[] = [];
    const specLine = (fieldId: string, text: string, margin: any = M, hi?: string) => {
      if (fieldEnabled('audio-backline', fieldId)) chunks.push({ id: fieldId, nodes: [{ ...hLabel(text), margin, ...(hi ? { background: hi } : {}) }] });
    };
    if (cfgSpecs.cableSpec) specLine('audio-backline.cable', `${t('pdf.ab.cable')}: ${cfgSpecs.cableSpec}`, M, lineHi((cfgSpecs as any).cableSpecProvider));
    if (cfgSpecs.stageCableSpec) specLine('audio-backline.stage-cable', `${t('pdf.ab.stageCable')}: ${cfgSpecs.stageCableSpec}`, M, lineHi((cfgSpecs as any).stageCableSpecProvider));
    if (cfgSpecs.audioCablesDetails) specLine('audio-backline.dmx', `${t('pdf.ab.audioCables')}: ${cfgSpecs.audioCablesDetails}`, M, lineHi((cfgSpecs as any).audioCablesDetailsProvider));
    if (cfgSpecs.networkRequirements) specLine('audio-backline.network', `${t('pdf.ab.network')}: ${cfgSpecs.networkRequirements}`, M, lineHi((cfgSpecs as any).networkRequirementsProvider));
    if (cfgSpecs.powerRequirements) specLine('audio-backline.power', `${t('pdf.ab.power')}: ${cfgSpecs.powerRequirements}`, M, lineHi((cfgSpecs as any).powerRequirementsProvider));
    if (chunks.length === 0) chunks.push({ id: null, nodes: [{ text: t('pdf.ab.noneSpecified'), margin: M4 }] });
    return [sectionTitle(secTitle('audio-backline', t('pdf.section.audio-backline')), { pageBreakBefore: secPbb('audio-backline') }), ...orderChunks('audio-backline', chunks)];
  };

  const buildWireless = (): Content[] | null => {
    // The Microphones / Frequency specs used to print under AUDIO & BACKLINE
    // (v0.642 movement, 2026-10-07): they belong to the Wireless Transmitters
    // section (the config page's "Wireless Microphones" block). Each line is a
    // NAMED chunk so it renders at its own field position — in the default
    // layout: specs first, then the transmitters table. A line is omitted when
    // the config value is empty (unchanged behaviour).
    const contentById: Record<string, Content[]> = {};
    if (cfgSpecs.microphonesSpecs) {
      contentById['wireless.mics'] = [{ ...hLabel(`${t('pdf.ab.mics')}: ${cfgSpecs.microphonesSpecs}`), margin: M, ...(lineHi((cfgSpecs as any).microphonesSpecsProvider) ? { background: lineHi((cfgSpecs as any).microphonesSpecsProvider) } : {}) }];
    }
    if (cfgSpecs.frequencyRange) {
      contentById['wireless.frequency'] = [{ ...hLabel(`${t('pdf.ab.frequency')}: ${cfgSpecs.frequencyRange}`), margin: M, ...(lineHi((cfgSpecs as any).frequencyRangeProvider) ? { background: lineHi((cfgSpecs as any).frequencyRangeProvider) } : {}) }];
    }
    const nodes = blockSectionNodes('wireless', 'wireless.table', wirelessRows.length > 0 ? [centerTableIfNarrow({
      table: {
        headerRows: 1,
        // v0.643 (user 2026-10-07): Person(60) and Instrument(80) are narrow on
        // purpose so the Transmitter column ('*') takes all the remaining width
        // (~275 pt of the 515 pt usable page) and its longest real value stays
        // on ONE line — "Beltpack + jack (Shure GLXD16)" (30 chars). Measured on
        // real data: longest person "Kristina" ≈50 pt, longest instrument
        // "Guitar GP-50" ≈70 pt, longest transmitter ≈164 pt. Do NOT
        // re-symmetrize these back to ['*', '*', '*', 45, 55]: that starved the
        // Transmitter column and wrapped the text onto two lines.
        widths: [60, 80, '*', 45, 55],
        body: [
          [
            headCell(t('pdf.col.person')),
            headCell(t('pdf.col.instrument')),
            headCell(t('pdf.col.transmitter')),
            headCell(t('pdf.col.micBy'), { center: true }),
            headCell(t('pdf.col.rxBy'), { center: true }),
          ],
          ...wirelessRows,
        ],
      },
      layout: TABLE_LAYOUT,
      margin: [0, 0, 0, 8] as [number, number, number, number],
    } as Content, [60, 80, '*', 45, 55])] : null, contentById);
    // Auto-hide rule extended (v0.642): the section prints when the table has
    // rows OR a spec value (mic / frequency) is set OR an enabled free-text
    // block has content — a lone spacer still never prints a bare heading.
    if (!nodes) return null;
    return [
      sectionTitle(secTitle('wireless', t('pdf.section.wireless')), { pageBreakBefore: secPbb('wireless') }),
      ...nodes,
    ];
  };

  const buildBackline = (): Content[] | null => {
    const nodes = blockSectionNodes('backline', BACKLINE_COLUMNS.map((c) => c.id), BLOCK_backlineRows ? [centerTableIfNarrow({
      // ⚠️ Header / cells / WIDTHS are all built together from the active
      // columns (v0.641) — the widths array mirrors the visible columns, so
      // hiding or reordering a column keeps the table legible (the historical
      // bug: a quantity column left carrying '*' rendered enormous — user
      // 2026-09-19). Default order Qty(40) | Type(*) | Item(*) | Model(*) |
      // Provided by(70) | Notes(*).
      table: { headerRows: 1, widths: BLOCK_backlineWidths, body: BLOCK_backlineRows },
      layout: TABLE_LAYOUT,
      margin: [0, 0, 0, 8] as [number, number, number, number],
    } as Content, BLOCK_backlineWidths)] : null);
    if (!nodes) return null;
    return [
      sectionTitle(secTitle('backline', t('pdf.section.backline')), { pageBreakBefore: secPbb('backline') }),
      ...nodes,
    ];
  };

  const buildMicStands = (): Content[] | null => {
    // The 4 columns form ONE logical table (v0.648): blockIds = the 4 column ids,
    // so the table is emitted ONCE at the position of the first enabled column
    // (spacers / free text before it print ABOVE, after it print BELOW). Widths
    // mirror the visible columns (BLOCK_standWidths) — never re-coded here.
    const nodes = blockSectionNodes('mic-stands', MIC_STANDS_COLUMNS.map((c) => c.id), BLOCK_standRows ? [centerTableIfNarrow({
      table: { headerRows: 1, widths: BLOCK_standWidths, body: BLOCK_standRows },
      layout: TABLE_LAYOUT,
      margin: [0, 0, 0, 8] as [number, number, number, number],
    } as Content, BLOCK_standWidths)] : null);
    if (!nodes) return null;
    return [
      sectionTitle(secTitle('mic-stands', t('pdf.section.mic-stands')), { pageBreakBefore: secPbb('mic-stands') }),
      ...nodes,
    ];
  };

  const buildMicProvided = (): Content[] | null => {
    const nodes = blockSectionNodes('mic-provided', 'mic-provided.summary', BLOCK_micProvided
      ? [{ text: BLOCK_micProvided, margin: M4 } as Content]
      : null);
    if (!nodes) return null;
    return [
      sectionTitle(secTitle('mic-provided', t('pdf.section.mic-provided')), { pageBreakBefore: secPbb('mic-provided') }),
      ...nodes,
    ];
  };

  const buildStageplan = (): Content[] => [
    sectionTitle(secTitle('stageplan', t('pdf.section.stageplan')), { pageBreakBefore: secPbb('stageplan') }),
  ];

  const buildInputList = (): Content[] => {
    const out: Content[] = [sectionTitle(secTitle('input-list', t('pdf.section.input-list')), { pageBreakBefore: secPbb('input-list') })];
    // Input List table (full table, holes for non-selected people)
    if (allTracks.length > 0) {
      out.push(riderTableNode({
        headerRows: 1,
        widths: inputColumns.map((c) => c.width),
        body: inputListRows,
      }, INPUT_LIST_TABLE_LAYOUT, [0, 0, 0, 8]));
    } else {
      out.push({ text: t('pdf.misc.noTracks'), margin: M4, color: '#999' });
    }
    // PATCH-MARKS LEGEND (2026-10-23, user v2): one line PER colour — a swatch
    // square + that colour's own text (from texts.inputListPatchLegends,
    // keyed by lowercase hex). Legacy single-text (inputListPatchLegend) is
    // still honoured as the fallback for all colours. Colours come from BOTH
    // lists (input + output tracks — same colour = same destination).
    const usedMarks = [...new Set([...allTracks, ...outputTracks].map((tr) => (tr as any).patchMark).filter(Boolean))] as string[];
    if (usedMarks.length > 0) {
      const legends = ((L.texts as any).inputListPatchLegends || {}) as Record<string, string>;
      const legacy = (L.texts as any).inputListPatchLegend as string | undefined;
      const rows = usedMarks.map((color) => {
        const key = color.toLowerCase();
        const text = legends[key] ?? legends[color] ?? (legacy || '').trim();
        return { color, text };
      }).filter((r) => r.text);
      if (rows.length > 0) {
        out.push({
          stack: rows.map(({ color, text }) => ({
            table: {
              widths: [10, '*'],
              body: [[
                { text: '', fillColor: color, border: [true, true, true, true], borderColor: '#666' },
                { text, color: L.style.textColor || '#333' },
              ]],
            },
            layout: 'noBorders',
            margin: [0, 2, 0, 2],
          })),
        });
      }
    }
    return out;
  };

  const buildOutputList = (): Content[] => {
    const out: Content[] = [sectionTitle(secTitle('output-list', t('pdf.section.output-list')), { pageBreakBefore: secPbb('output-list') })];
    // Output List (returns / IEMs / sends)
    if (outputTracks.length > 0) {
      out.push(riderTableNode({
        headerRows: 1,
        widths: outputColumns.map((c) => c.width),
        body: outputRows,
      }, TABLE_LAYOUT, [0, 0, 0, 8]));
    } else {
      out.push({ text: t('pdf.misc.noOutputTracks'), margin: M4, color: '#999' });
    }
    // OUTPUT PATCH-MARKS LEGEND (2026-10-23, user v3): same per-colour swatch
    // + text under the Output List, from texts.outputListPatchLegends — a
    // colour can mean something DIFFERENT on the outputs than on the inputs
    // (separate editable text per list; fallback to the input legend for
    // back-compat when the output one was never written).
    const usedOutMarks = [...new Set(outputTracks.map((tr) => (tr as any).patchMark).filter(Boolean))] as string[];
    if (usedOutMarks.length > 0) {
      const outLegends = ((L.texts as any).outputListPatchLegends || {}) as Record<string, string>;
      const inLegends = ((L.texts as any).inputListPatchLegends || {}) as Record<string, string>;
      const legacy = (L.texts as any).inputListPatchLegend as string | undefined;
      const wroteOut = Object.keys(outLegends).length > 0;
      const rows = usedOutMarks.map((color) => {
        const key = color.toLowerCase();
        const text = outLegends[key] ?? outLegends[color]
          ?? (wroteOut ? '' : (inLegends[key] ?? inLegends[color] ?? (legacy || '').trim()));
        return { color, text };
      }).filter((r) => r.text);
      if (rows.length > 0) {
        out.push({
          stack: rows.map(({ color, text }) => ({
            table: {
              widths: [10, '*'],
              body: [[
                { text: '', fillColor: color, border: [true, true, true, true], borderColor: '#666' },
                { text, color: L.style.textColor || '#333' },
              ]],
            },
            layout: 'noBorders',
            margin: [0, 2, 0, 2],
          })),
        });
      }
    }
    return out;
  };

  const buildRackUsage = (): Content[] | null => {
    // Rack usage summary: for every band rack (merged across all transport
    // presets, same source the instrument editors show) print the used/capacity
    // channel counts and the compact occupant list. Complementary to the
    // individual Input/Output lists — this is the per-rack occupancy picture.
    const racks = mergeStageRacks(config.globalRacks, config.specs as never, transport);
    if (!racks || racks.length === 0) return null;
    // Sidefills du transport DU RIDER uniquement (même défaut que la Output List,
    // corrigé le 2026-10-15) : l'ancienne union sur les 4 presets ramenait les
    // sidefills supprimés, encore présents dans les autres modes de transport. La
    // dédup par id reste en garde-fou (un même id deux fois dans UN tableau = donnée
    // incohérente) mais elle n'a plus de rôle inter-presets.
    const sidefills: SidefillConfig[] = [];
    const seenSfIds = new Set<string>();
    const sidefillList = transport ? config.specs?.[transport]?.sidefills : undefined;
    for (const sf of Array.isArray(sidefillList) ? sidefillList : []) {
      if (!sf || !sf.id || seenSfIds.has(sf.id)) continue;
      seenSfIds.add(sf.id);
      sidefills.push(sf);
    }
    // Le mix principal compte aussi comme occupant (paire stéréo sur son rack).
    const paMix = (cfgSound.paMixRackId && (cfgSound.paMixOutStart || 0) > 0)
      ? { rackId: cfgSound.paMixRackId, ch: cfgSound.paMixOutStart as number }
      : undefined;
    const usage = computeRackUsage({ musicians, crew, sidefills, paMix });
    // Per-COLUMN style override (each column is a layout field).
    const rackOv = (fid: string) => fieldOvById('rack-usage', fid);
    const body: any[] = [[
      headCell(t('pdf.rackUsage.rack'), { styleOverride: rackOv('rack-usage.rack') }),
      headCell(t('pdf.rackUsage.inputs'), { center: true, styleOverride: rackOv('rack-usage.inputs') }),
      headCell(t('pdf.rackUsage.outputs'), { center: true, styleOverride: rackOv('rack-usage.outputs') }),
      headCell(t('pdf.rackUsage.occupants'), { styleOverride: rackOv('rack-usage.occupants') }),
    ]];
    let rackRowIndex = 0;
    for (const rack of racks) {
      const zebra = rackRowIndex++ % 2 === 1;
      const { inputs, outputs } = rackCapacities(rack as StageRack);
      const usedIn = usage.inputs[rack.id] ? Object.keys(usage.inputs[rack.id]).length : 0;
      const usedOut = usage.outputs[rack.id] ? Object.keys(usage.outputs[rack.id]).length : 0;
      const overIn = usedIn > inputs;
      const overOut = usedOut > outputs;
      // Compact occupant list (distinct labels) across inputs + outputs.
      const occupants = new Set<string>();
      for (const ch of Object.values(usage.inputs[rack.id] ?? {})) occupants.add(ch);
      for (const ch of Object.values(usage.outputs[rack.id] ?? {})) occupants.add(ch);
      const occupantList = Array.from(occupants).join(', ') || t('pdf.rackUsage.free');
      body.push([
        dataCell(rack.name || rack.id, { zebra, styleOverride: rackOv('rack-usage.rack'), uppercase: secUppercase('rack-usage') }),
        {
          ...dataCell(`${usedIn}/${inputs}${overIn ? ' ⚠' : ''}`, { center: true, zebra, styleOverride: rackOv('rack-usage.inputs'), uppercase: secUppercase('rack-usage') }),
          // Over-capacity is the ONLY intentional emphasis in this table.
          color: overIn ? '#c0392b' : undefined,
          bold: overIn,
          ...fieldStyleProps(rackOv('rack-usage.inputs')),
        },
        {
          ...dataCell(`${usedOut}/${outputs}${overOut ? ' ⚠' : ''}`, { center: true, zebra, styleOverride: rackOv('rack-usage.outputs'), uppercase: secUppercase('rack-usage') }),
          color: overOut ? '#c0392b' : undefined,
          bold: overOut,
          ...fieldStyleProps(rackOv('rack-usage.outputs')),
        },
        dataCell(occupantList, { fontSize: 8, zebra, styleOverride: rackOv('rack-usage.occupants'), uppercase: secUppercase('rack-usage') }),
              ]);
              if (rack.notes) {
                body.push([
                  { text: [{ text: `${t('pdf.rackUsage.notes').toUpperCase()}: `, bold: true }, rack.notes], colSpan: 4, fontSize: 8, color: '#666', margin: [0, 2, 0, 6] },
                  {}, {}, {},
                ]);
              }
            }
            const out: Content[] = [sectionTitle(secTitle('rack-usage', t('pdf.section.rack-usage')), { pageBreakBefore: secPbb('rack-usage') })];
    // Per-column width override (v0.644): the four columns are layout fields, so
    // a numeric `field.width` wins over the historical default (90/60/70/'*').
    const rw = (fid: string, dflt: number | string): number | string => {
      const f = L.sections.find((s) => s.id === 'rack-usage')?.fields?.find((x) => x.id === fid);
      return typeof f?.width === 'number' ? f.width : dflt;
    };
    out.push(riderTableNode({ headerRows: 1, widths: [rw('rack-usage.rack', 90), rw('rack-usage.inputs', 60), rw('rack-usage.outputs', 70), rw('rack-usage.occupants', '*')], body }, TABLE_LAYOUT, [0, 0, 0, 8]));
    return out;
  };

  const buildStagePlot = (): Content[] | null => {
    // Stage plot image (start of page 3 by default). Since v0.640 the section
    // follows the LAYOUT field order so the spacers / free-text blocks inserted
    // in the Rider Layout editor render around the image (or the placeholder
    // fallback) — they used to be persisted but never emitted. The block keeps
    // its historical fallbacks: a configured plot without a rasterized PNG shows
    // the "available" hint, and a band with no stage plot keeps the "none" hint.
    const blockNodes: Content[] = stagePlot
      ? (stagePlotImage
          ? [{ image: stagePlotImage, width: 500, alignment: 'center', margin: [0, 8, 0, 8] as [number, number, number, number] } as Content]
          : [{ text: t('pdf.misc.stagePlotAvailable'), margin: M4, color: '#999', italics: true } as Content])
      : [{ text: t('pdf.misc.noStagePlot'), margin: M4, color: '#999', italics: true } as Content];
    const ordered = blockSectionNodes('stage-plot', 'stage-plot.image', blockNodes);
    if (!ordered) return null;
    return [
      sectionTitle(secTitle('stage-plot', t('pdf.section.stage-plot')), { pageBreakBefore: secPbb('stage-plot') }),
      ...ordered,
    ];
  };

  const buildLights = (): Content[] => {
    const chunks: SectionChunk[] = [];
    const lightLine = (fieldId: string, text: string, margin: any = M) => {
      if (fieldEnabled('lights', fieldId)) chunks.push({ id: fieldId, nodes: [{ ...hLabel(text), margin }] });
    };
    lightLine('lights.console', `${t('pdf.lights.console')}: ${cfgLights.console || 'TBD'}`);
    lightLine('lights.haze', `${t('pdf.lights.haze')}: ${cfgLights.hazeMachine || 'TBD'}`);
    if (fieldEnabled('lights', 'lights.fixtures')) {
      const fx: any = cfgLights.fixtures || {};
      // Fixtures: one small table per lighting category (like the backline
      // table). The whole block belongs to the 'lights.fixtures' field so it
      // moves as ONE unit when the field is reordered in the layout.
      // v0.648: the tables are built MIRROR-FIRST from the ACTIVE fixture
      // COLUMNS of the layout (LIGHT_FIXTURE_COLUMNS — a DIFFERENT kind of id
      // from the section's content lines): header + every row + widths derive
      // from the same column list, so hiding / reordering / resizing a fixture
      // column moves its header, its cells AND its width together.
      const fixtureColumns = activeListColumns(L, 'lights', LIGHT_FIXTURE_COLUMNS);
      const fixtureNodes: Content[] = [];
      if (fixtureColumns.length > 0) {
        for (const { value: cat, label } of LIGHT_FIXTURE_CATEGORIES) {
          const items = Array.isArray(fx[cat]) ? fx[cat] : [];
          if (items.length === 0) continue;
          const body: any[] = [
            fixtureColumns.map((c) => headCell(colLabel(c.id), { center: c.center })),
          ];
          items.forEach((it: any, idx: number) => {
            const zebra = idx % 2 === 1;
            const row: FixtureRow = { qty: it.quantity || 1, model: it.type || '', description: it.label || '', notes: it.notes || '' };
            body.push(fixtureColumns.map((c) => dataCell(c.get(row), { center: c.center, zebra, uppercase: secUppercase('lights') })));
          });
          fixtureNodes.push({
            text: label.toUpperCase(),
            style: { bold: true, fontSize: 10, margin: [0, 6, 0, 2] },
          });
          fixtureNodes.push(riderTableNode({ headerRows: 1, widths: fixtureColumns.map((c) => c.width), body }, TABLE_LAYOUT, [0, 0, 0, 6]));
        }
        // If no fixtures entered, show a fallback line so the section isn't empty.
        const anyFixtures = LIGHT_FIXTURE_CATEGORIES.some(({ value: cat }) => (Array.isArray(fx[cat]) ? fx[cat].length : false));
        if (!anyFixtures) fixtureNodes.push({ ...hLabel(t('pdf.lights.fixturesNone')), margin: M });
      }
      chunks.push({ id: 'lights.fixtures', nodes: fixtureNodes });
    }
    if (cfgLights.dmxLine) lightLine('lights.dmx', `${t('pdf.lights.dmx')}: ${cfgLights.dmxLine}`);
    if (cfgLights.notes) lightLine('lights.notes', `${t('pdf.lights.notes')}: ${cfgLights.notes}`, M4);
    // Video format / resolution — two Lights-config fields reported as ONE line.
    const videoBits = [cfgLights.videoFormat, cfgLights.videoResolution].map((v) => (v || '').trim()).filter(Boolean);
    if (videoBits.length > 0) lightLine('lights.video', `${t('pdf.lights.video')}: ${videoBits.join(' / ')}`);
    // Power requirements — the Lights config's `powerNotes` free text.
    if ((cfgLights.powerNotes || '').trim()) lightLine('lights.power', `${t('pdf.lights.power')}: ${cfgLights.powerNotes}`, M4);
    return [sectionTitle(secTitle('lights', t('pdf.section.lights')), { pageBreakBefore: secPbb('lights') }), ...orderChunks('lights', chunks)];
  };

  const buildLightPlot = (): Content[] | null => {
    // Light plot renders when at least one image is available AND its layout
    // field is enabled. Emitted under the field id `light-plot.image` (ONE id
    // for the whole section — so the existing layout editor fields and saved
    // layouts keep working; never introduce a second field id here).
    //
    // Order: the legacy single `imageData` FIRST (unchanged behaviour), then
    // one centered image per multi-plot REFERENCE (config.lights[transport].plots),
    // in the list's order, the file name as a caption when present.
    //
    // v0.640: the whole block is emitted THROUGH the layout field order so the
    // spacers / free-text blocks inserted in the editor render too (they used to
    // be persisted but never emitted — the old early `return null` ran before
    // the field order was ever consulted). Auto-hide is preserved: no image AND
    // no free-text content → the section is not printed.
    const nodes: Content[] = [];
    if (cfgLights.imageData) {
      nodes.push({
        image: cfgLights.imageData, width: 500, alignment: 'center',
        margin: [0, 8, 0, 8] as [number, number, number, number],
      } as Content);
    }
    for (const plot of (Array.isArray(lightPlotImages) ? lightPlotImages : [])) {
      if (!plot || !plot.dataUrl) continue;
      nodes.push({
        image: plot.dataUrl, width: 500, alignment: 'center',
        margin: [0, 8, 0, 8] as [number, number, number, number],
      } as Content);
      if (plot.name) {
        nodes.push({
          text: plot.name, alignment: 'center', fontSize: 9, color: '#666666',
          margin: [0, 0, 0, 12] as [number, number, number, number],
        } as Content);
      }
    }
    const ordered = blockSectionNodes('light-plot', 'light-plot.image', nodes.length > 0 ? nodes : null);
    if (!ordered) return null; // nothing to show → auto-hide (legacy behaviour)
    return [
      sectionTitle(secTitle('light-plot', t('pdf.section.light-plot')), { pageBreakBefore: secPbb('light-plot') }),
      ...ordered,
    ];
  };

  // Stage Design — the band's descriptive stage-design notes (complementary
  // to the drawn Stage Plot). Rendered only when at least one field is set.
  // Field ids tagged per field so the layout engine can enable/reorder them.
  const buildStageDesign = (): Content[] | null => {
    const sd = cfgStageDesign || {};
    const risers = Array.isArray(sd.risers) ? sd.risers : [];
    // Risers drawn in the selected stage plot (iconId 'risers', RiserConfig)
    // are surfaced in the PDF too, with their dimensions + quantity, so the
    // platforms the techs actually built appear in the rider (2026-08-26).
    const plotRiserLines: Content[] = [];
    if (stagePlot && Array.isArray((stagePlot as any).elements)) {
      // Risers merged into the editable structured risers list are already
      // rendered above; skip them here so they don't appear twice in the PDF.
      const editableNames = new Set(
        risers.map((r: any) => (r && r.name ? String(r.name).trim() : '')).filter(Boolean),
      );
      const toM = (v: number) => Number.isInteger(v / 100) ? `${v / 100}m` : `${(v / 100).toFixed(2)}m`;
      for (const el of (stagePlot as any).elements) {
        if (!el || el.iconId !== 'risers') continue;
        const cfgR = ((el.config ?? {}) as unknown) as RiserConfig;
        const items = riserItems(cfgR);
        const first = items[0];
        if (!first) continue;
        const qty = typeof cfgR?.count === 'number' && cfgR.count > 0 ? cfgR.count : 1;
        const name = el.label && el.label.trim() && el.label !== 'Risers' ? el.label : t('pdf.sd.riser');
        if (editableNames.has(String(name).trim())) continue;
        const dims = `${toM(first.width)} × ${toM(first.depth)} × ${toM(first.height)}`;
        plotRiserLines.push({ ...hLabel(`${name}: ${qty} × ${dims}`), margin: M });
      }
    }
    const hasAny = !!(sd.stageDimensions || sd.backdrop || sd.backdropDimensions
      || risers.some((r: any) => r && (r.name || r.width || r.depth || r.height || r.position || r.quantity))
      || plotRiserLines.length > 0
      || sd.rug || sd.accessories || sd.chairs || sd.tools || sd.notes);
    if (!hasAny) return null;
    const chunks: SectionChunk[] = [];
    const push = (fieldId: string, text: string) => {
      if (!text) return;
      if (!fieldEnabled('stage-design', fieldId)) return;
      chunks.push({ id: fieldId, nodes: [{ ...hLabel(text), margin: M }] });
    };
    push('stage-design.dimensions', sd.stageDimensions ? `${t('pdf.sd.dimensions')}: ${sd.stageDimensions}` : '');
    // ONE layout field = ONE config value (same rule as general-info): the
    // backdrop and its dimensions were merged into a single 'backdrop' line, so
    // the editor's 'Backdrop dimensions' field did nothing (toggling it had no
    // effect and disabling 'Backdrop' silently dropped the dimensions too).
    push('stage-design.backdrop', sd.backdrop ? `${t('pdf.sd.backdrop')}: ${sd.backdrop}` : '');
    push('stage-design.backdrop-dimensions', sd.backdropDimensions ? `${t('pdf.sd.backdropDimensions')}: ${sd.backdropDimensions}` : '');
    if (fieldEnabled('stage-design', 'stage-design.risers') && (risers.length > 0 || plotRiserLines.length > 0)) {
      // The risers list (structured + stage-plot risers) is ONE field unit, so
      // reordering it in the layout moves the whole block.
      const riserNodes: Content[] = [];
      for (const r of risers) {
        if (!r || !(r.name || r.width || r.depth || r.height || r.position || r.quantity)) continue;
        const dims = [r.width, r.depth, r.height].filter(Boolean).join(' × ');
        const spec = [r.quantity ? `${r.quantity} × ${dims}` : dims, r.position].filter(Boolean).join(' — ');
        // Routed through hLabel: the label before the first ':' renders BOLD +
        // UPPERCASE (RISER / EGO RISER …) while the spec keeps its normal case
        // and the ORDER is unchanged (name : qty × dims — position). A riser
        // always carries a ':' (the name, or the default 'pdf.sd.riser' label,
        // is followed by ':'), so the label is always uppercased.
        riserNodes.push({ ...hLabel(`${r.name || t('pdf.sd.riser')}: ${spec}`), margin: M });
      }
      riserNodes.push(...plotRiserLines);
      chunks.push({ id: 'stage-design.risers', nodes: riserNodes });
    }
    push('stage-design.rug', sd.rug ? `${t('pdf.sd.rug')}: ${sd.rug}` : '');
    push('stage-design.accessories', sd.accessories ? `${t('pdf.sd.accessories')}: ${sd.accessories}` : '');
    push('stage-design.chairs', sd.chairs ? `${t('pdf.sd.chairs')}: ${sd.chairs}` : '');
    push('stage-design.tools', sd.tools ? `${t('pdf.sd.tools')}: ${sd.tools}` : '');
    push('stage-design.notes', sd.notes ? `${t('pdf.sd.notes')}: ${sd.notes}` : '');
    return [sectionTitle(secTitle('stage-design', t('pdf.section.stage-design')), { pageBreakBefore: secPbb('stage-design') }), ...orderChunks('stage-design', chunks)];
  };

  const builders: Record<PdfSectionId, () => Content[] | null> = {
    title: buildTitle,
    contact: buildContact,
    intro: buildIntro,
    'general-info': buildGeneralInfo,
    hospitality: buildHospitality,
    'technical-rider': buildTechnicalRider,
    sound: buildSound,
    iem: buildIem,
    monitors: buildMonitors,
    'audio-backline': buildAudioBackline,
    wireless: buildWireless,
    backline: buildBackline,
    'mic-stands': buildMicStands,
    'mic-provided': buildMicProvided,
    stageplan: buildStageplan,
    'input-list': buildInputList,
    'output-list': buildOutputList,
    'rack-usage': buildRackUsage,
    'stage-plot': buildStagePlot,
    lights: buildLights,
    'light-plot': buildLightPlot,
    'stage-design': buildStageDesign,
  };

  // pdfmake has NO per-section defaultStyle: the document font/size/color are
  // global, so a section that overrides them gets the value stamped on every
  // text node it emitted (body lines, table cells, bullet items) — never on a
  // node that already set its own fontSize/color/font (headings, titles, free
  // text), which are styled inside the builder. No override key → the nodes are
  // returned untouched (default rendering, byte-identical).
  const applySectionStyle = (nodes: Content[], ov: Partial<PdfLayoutStyle>): Content[] => {
    const { fontFamily, bodyFontSize, textColor } = ov;
    if (!fontFamily && bodyFontSize === undefined && !textColor) return nodes;
    const walk = (node: any): void => {
      if (Array.isArray(node)) {
        node.forEach(walk);
        return;
      }
      if (!node || typeof node !== 'object') return;
      if (node.text !== undefined) {
        const style = node.style && typeof node.style === 'object' ? node.style : null;
        if (fontFamily && node.font === undefined && !(style && style.font !== undefined)) node.font = fontFamily;
        if (bodyFontSize !== undefined && node.fontSize === undefined && !(style && style.fontSize !== undefined)) node.fontSize = bodyFontSize;
        if (textColor && node.color === undefined && !(style && style.color !== undefined)) node.color = textColor;
      }
      for (const k of ['stack', 'columns', 'ul', 'ol']) {
        if (node[k]) walk(node[k]);
      }
      if (node.table && node.table.body) walk(node.table.body);
    };
    walk(nodes);
    return nodes;
  };

  // Iterate the layout's ordered section list: enabled sections call their
  // builder; null results (no data) are skipped. This replaces the old
  // hard-coded push sequence — order, toggles, titles and page breaks now
  // come from the PdfLayout.
  for (const s of L.sections) {
    if (!s.enabled) continue;
    // Per-section style: the section's styleOverride (if any) is layered over
    // the global style FOR THIS BUILDER ONLY — the next iteration resets it.
    secStyle = { ...L.style, ...(s.styleOverride || {}) };
    let block = builders[s.id]?.();
    if (block && s.styleOverride) {
      block = applySectionStyle(block, s.styleOverride);
    }
    if (block) content.push(...block);
  }

  // Running header / footer: sent to the server as a SERIALIZABLE spec — a
  // pdfmake closure would be dropped by JSON.stringify (F2). {bandName} /
  // {versionLabel} are resolved here, {page}/{pages} are left for the server,
  // which rebuilds the pdfmake function and substitutes them at render time.
  const runnerFill = (tpl: string): string =>
    (tpl || '').replace(/\{(\w+)\}/g, (_, k: string) => {
      if (k === 'bandName') return bandName ?? 'TECHNICAL';
      if (k === 'versionLabel') return versionLabel;
      // {versionDetail} is advertised in the editor's placeholder list for the
      // header/footer too — resolve it here; anything the CLIENT cannot resolve
      // is emptied server-side, which would silently blank the running header.
      if (k === 'versionDetail') return versionDetail;
      return `{${k}}`; // {page}/{pages} … resolved server-side
    });
  const runnerSpec = (left: string, center: string, right: string, margin: [number, number, number, number]) => ({
    left: runnerFill(left),
    center: runnerFill(center),
    right: runnerFill(right),
    margin,
    fontSize: L.style.footerFontSize,
    color: L.style.footerColor,
  });

  return {
    content,
    defaultStyle: {
      font: L.style.fontFamily ?? 'Roboto',
      fontSize: L.style.bodyFontSize,
      color: L.style.textColor,
    },
    pageSize: 'A4',
    pageMargins: L.style.pageMargins,
    ...(L.texts.headerEnabled
      ? {
          headerSpec: runnerSpec(
            L.texts.headerLeftTemplate,
            L.texts.headerCenterTemplate,
            L.texts.headerRightTemplate,
            [40, 10, 40, 0],
          ),
        }
      : {}),
    // Footer: emitted unless the layout explicitly turns it off (footerEnabled
    // absent = enabled, so every layout saved before the toggle keeps its footer).
    ...(L.texts.footerEnabled !== false
      ? {
          footerSpec: runnerSpec(
            L.texts.footerLeftTemplate,
            L.texts.footerCenterTemplate,
            L.texts.footerRightTemplate,
            [40, 12, 40, 0],
          ),
        }
      : {}),
  };
}

export default function RiderPage() {
  const { t: ui } = useTranslation();
  const { config, updateRider, reload } = useConfig();
  // Capability-aware write gate (lot A2-CAP, 2026-09-25). `rider.transport` /
  // `activeMusicianIds` / `activeCrewIds` persist through useConfig →
  // PUT /api/bands/:id/rider/config = the rider CONTENT, delegated to
  // `content.rider`. The AI import goes to `/api/ai/apply`, still mounted AFTER
  // the read-only guard and NOT delegated to any capability, so it follows the
  // blanket ceiling.
  const writeGate = useWriteGate();
  const { user } = useAuth();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The stage plot IS rider content: `/api/stageplots` (create/rename/delete) is
  // exempted from the global read-only guard for `content.rider` too (conditional
  // pre-guard mount in server/index.js, band resolved from the ENTITY), so this
  // path is gated by the SAME capability — never by the blanket ceiling, which
  // would freeze the very crew member the capability exists for.
  // `/api/ai/apply` (AI import) alone stays non-delegated → blanket ceiling.
  const canWriteStagePlots = canEditContent;
  const canWriteNonDelegated = writeGate.canWrite;
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { musicians } = useMusicians();
  const { crew } = useCrew();
  const { activeProfileId, getProfile } = useProfileContext();
  const navigate = useNavigate();
  const base = activeProfileId ? `/band/${activeProfileId}` : '';
  // L2 (2026-10-12) — LECTURE PURE. Un membre qui ne détient NI `content.rider`, NI une
  // SECTION du config, NI le rôle éditeur (le cas `viewer`) ne doit pas voir un
  // formulaire grisé : l'écran LIT (composant `RiderReadOnlyView`), le PDF reste
  // téléchargeable, et « Import AI » (action d'écriture) disparaît.
  // ⚠️ Le prédicat est celui de la ROUTE visée (`canSeeRiderConfig` = showcase,
  // superadmin, owner/editor, ou la capacité / la section réellement accordée) — JAMAIS
  // un plafond global : un titulaire de section garde l'écran éditable complet
  // (§8 de `client-write-gate-2026.md`).
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;
  const hasRiderSurface = canSeeRiderConfig({
    isSuperAdmin: user?.global_role === 'superadmin',
    isShowcase: !!user?.is_showcase || isShowcaseMode(),
    bandRole: activeProfile?.my_role ?? null,
    permissions: activeProfile?.permissions ?? null,
  });
  const pureReadonly = !hasRiderSurface;
  // Defensive: never let a missing/partial config.rider crash the render.
  const riderCfg = config.rider ?? createDefaultAppConfig().rider;
  const [stagePlots, setStagePlots] = useState<StagePlotListItem[]>([]);
  const [selectedStagePlotId, setSelectedStagePlotId] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  // The docDef that produced the current preview — passed to <PdfPreview> so
  // every page renders on iOS (iframe shows only page 1). Kept alongside the
  // blob url (used for download). (2026-08-13)
  const [pdfDoc, setPdfDoc] = useState<TDocumentDefinitions | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  // Logo for the PDF header, fetched directly from the band record so we can
  // pick the valid field (image_url preferred over the possibly-corrupted `image`).
  const [bandLogoUrl, setBandLogoUrl] = useState<string | null>(null);
  // team_member id -> { diet, dietNotes } for the hospitality PDF section.
  // Diet lives on the Team identity, so the PDF reads it from there by id.
  const [dietById, setDietById] = useState<Record<string, { diet?: string; dietNotes?: string }>>({});
  // Management / Advancing contact — the band's manager from the TEAM page
  // (personal info: first name + last name, email, phone live on team_members,
  // not on the rider crew cards).
  const [teamManager, setTeamManager] = useState<{ name?: string; email?: string; phone?: string } | null>(null);
  // Admin-edited PDF layout (global template + band override, merged by the
  // backend). Passed to buildPdfDoc so the rider PDF follows the Rider Editor.
  // Falls back to null → createDefaultPdfLayout() inside buildPdfDoc.
  const [pdfLayout, setPdfLayout] = useState<any | null>(null);
  // "Import your rider (AI)" panel visibility.
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!activeProfileId) return;
    apiGetTeam({ band: activeProfileId, tags: ['manager'] })
      .then((list) => {
        if (cancelled) return;
        const m = list[0];
        if (m) setTeamManager({ name: m.name || m.real_name || m.nickname || '', email: m.email || '', phone: m.phone || '' });
      })
      .catch(() => { /* non-fatal: fall back to the crew card */ });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Load the admin-edited PDF layout (global + band override merged) so the
  // rider PDF preview follows the Rider Editor. No row → null → defaults.
  useEffect(() => {
    let cancelled = false;
    setPdfLayout(null);
    if (!activeProfileId) return;
    apiGetPdfLayoutEffective(activeProfileId)
      .then((layout) => { if (!cancelled) setPdfLayout(layout); })
      .catch(() => { /* non-fatal: fall back to defaults */ });
    return () => { cancelled = true; };
  }, [activeProfileId]);


  // The two PREVIEW checkbox lists use the SAME order as everywhere else in
  // this page (main → session → guest, then `order`, then `name`) — they used
  // to sort by `order` alone (musicians) or not at all (crew).
  const sortedMusicians = useMemo(() => sortMusicians(musicians), [musicians]);
  const sortedCrew = useMemo(() => sortCrew(crew), [crew]);

  const toggleMusician = useCallback(
    (id: string) => {
      if (!canEditContent) return; // no `content.rider` → PUT /rider/config 403s
      const ids = riderCfg.activeMusicianIds || [];
      updateRider({
        activeMusicianIds: ids.includes(id)
          ? ids.filter((i) => i !== id)
          : [...ids, id],
      });
    },
    [riderCfg.activeMusicianIds, updateRider, canEditContent],
  );

  // Resolve backline image URLs (/static/uploads/...) to data URLs so the
  // server-side pdfmake can embed them. Data URLs pass through unchanged.
  // pdfmake under Node supports PNG/JPEG only — WebP/GIF/AVIF blobs are
  // re-encoded to PNG through a canvas so the PDF never fails.
  const resolveBacklineImages = useCallback(
    async <T extends { backline?: { imageUrl?: string }[] }>(list: T[]): Promise<T[]> => {
      const resolveImg = async (u: string | undefined): Promise<string | undefined> => {
        if (!u) return undefined;
        if (u.startsWith('data:')) {
          // pdfmake/Node supports PNG/JPEG data URLs only — re-encode the
          // others (WebP/GIF/AVIF) to PNG through a canvas.
          if (u.startsWith('data:image/png') || u.startsWith('data:image/jpeg')) return u;
          return await reencodeToPng(u);
        }
        try {
          const res = await fetch(u, { credentials: 'include' });
          if (!res.ok) return undefined;
          const blob = await res.blob();
          if (blob.type === 'image/webp' || blob.type === 'image/gif' || blob.type === 'image/avif') {
            // Re-encode to PNG via canvas (pdfmake/Node cannot embed these).
            const url = URL.createObjectURL(blob);
            try {
              return await new Promise<string | undefined>((resolve) => {
                const img = new Image();
                const timer = setTimeout(() => { URL.revokeObjectURL(url); resolve(undefined); }, 8000);
                img.onload = () => {
                  clearTimeout(timer);
                  URL.revokeObjectURL(url);
                  try {
                    const canvas = document.createElement('canvas');
                    canvas.width = img.width || 400;
                    canvas.height = img.height || 400;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) { resolve(undefined); return; }
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/png'));
                  } catch { resolve(undefined); }
                };
                img.onerror = () => { clearTimeout(timer); URL.revokeObjectURL(url); resolve(undefined); };
                img.src = url;
              });
            } catch {
              URL.revokeObjectURL(url);
              return undefined;
            }
          }
          return await new Promise<string | undefined>((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () => resolve(undefined);
            reader.readAsDataURL(blob);
          });
        } catch {
          return undefined;
        }
      };
      // Re-encode an arbitrary image data URL (WebP/GIF/AVIF) to PNG.
      const reencodeToPng = (dataUrl: string): Promise<string | undefined> =>
        new Promise((resolve) => {
          const img = new Image();
          const timer = setTimeout(() => resolve(undefined), 8000);
          img.onload = () => {
            clearTimeout(timer);
            try {
              const canvas = document.createElement('canvas');
              canvas.width = img.width || 400;
              canvas.height = img.height || 400;
              const ctx = canvas.getContext('2d');
              if (!ctx) { resolve(undefined); return; }
              ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
              resolve(canvas.toDataURL('image/png'));
            } catch { resolve(undefined); }
          };
          img.onerror = () => { clearTimeout(timer); resolve(undefined); };
          img.src = dataUrl;
        });
      return Promise.all(list.map(async (item) => ({
        ...item,
        backline: await Promise.all((item.backline || []).map(async (bl) => ({
          ...bl,
          imageUrl: await resolveImg(bl.imageUrl),
        }))),
      })));
    },
    [],
  );

  // Reset the preview whenever the active band changes so we never keep
  // showing another band's stale PDF blob.
  useEffect(() => {
    setPdfUrl(null);
    setPdfDoc(null);
    setPdfError(null);
  }, [activeProfileId]);

  // Generate PDF preview with debounce — rendered on the server (robust,
  // no client-side pdfmake Web Worker that can hang in some browsers).
  const genTokenRef = useRef(0);
  useEffect(() => {
    const token = ++genTokenRef.current;
    const timeout = setTimeout(async () => {
      // Only attempt a stage plot if one actually exists for this band.
      const selectedPlot = stagePlots.length > 0
        ? stagePlots.find((plot) => plot.id === selectedStagePlotId)?.data ?? null
        : null;
      let stageImage: string | null = null;
      if (selectedPlot) {
        try {
          // Handles both kinds: canvas (SVG→PNG) and imported image (data URL).
          stageImage = await stagePlotToPdfImage(selectedPlot);
        } catch (e) {
          console.warn('[RiderPage] stage plot rasterization failed, omitting image:', e);
          stageImage = null;
        }
      }
      let docDef;
      try {
        const profile = getProfile(activeProfileId ?? '');
        const [musiciansResolved, crewResolved] = await Promise.all([
          resolveBacklineImages<Musician>(musicians),
          resolveBacklineImages<CrewMember>(crew),
        ]);
        // Multi light plots: the SHARED union across the 4 transport modes
        // (read by the UI too), resolved to embeddable data URLs (guarded server
        // preview → PNG, one per PDF page). Legacy callers that pass nothing
        // keep the single `imageData` behaviour.
        const lightPlotImages = await resolveLightPlotsForPdf(
          activeProfileId,
          sharedLightPlots(config.lights),
        );
        docDef = buildPdfDoc(
          profile?.name,
          { transport: riderCfg.transport, activeMusicianIds: riderCfg.activeMusicianIds, activeCrewIds: riderCfg.activeCrewIds, requirements: null },
          config, musiciansResolved, crewResolved, selectedPlot, stageImage, bandLogoUrl, dietById,
          undefined,
          teamManager,
          undefined,
          pdfLayout,
          null,
          lightPlotImages,
        );
      } catch (err) {
        if (token !== genTokenRef.current) return;
        const message = err instanceof Error ? err.message : 'Failed to build rider';
        console.error('[RiderPage] buildPdfDoc failed:', err);
        setPdfError(message);
        setPdfUrl(null);
        return;
      }
      try {
        const url = await generatePdfBlobViaServer(docDef);
        if (token !== genTokenRef.current) {
          URL.revokeObjectURL(url);
          return;
        }
        setPdfError(null);
        setPdfDoc(docDef);
        setPdfUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return url;
        });
      } catch (err) {
        if (token !== genTokenRef.current) return;
        const message = err instanceof Error ? err.message : 'Unknown PDF generation error';
        console.error('PDF preview generation failed:', err);
        setPdfError(message);
        setPdfUrl(null);
      }
    }, 400);

    return () => clearTimeout(timeout);
  }, [config, musicians, crew, stagePlots, selectedStagePlotId, activeProfileId, bandLogoUrl, dietById, teamManager, pdfLayout]);

  useEffect(() => {
    if (!activeProfileId) {
      setStagePlots([]);
      setSelectedStagePlotId(null);
      return;
    }
    let cancelled = false;
    void listStagePlots({ bandId: activeProfileId }).then((result) => {
      if (cancelled) return;
      setStagePlots(result.items);
      setSelectedStagePlotId((current) => current && result.items.some((plot) => plot.id === current) ? current : result.items[0]?.id ?? null);
    }).catch((err) => reportSaveError('stageplots', err));
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Load the band logo for the PDF header straight from the DB record so we
  // can prefer the valid `image_url` field over a possibly-corrupted `image`.
  useEffect(() => {
    if (!activeProfileId) { setBandLogoUrl(null); return; }
    let cancelled = false;
    // Reset immediately so switching bands never shows the previous band's
    // logo while the new one loads (and the preview regen depends on this).
    setBandLogoUrl(null);
    void apiGetBand(activeProfileId).then(async (band) => {
      if (cancelled) return;
      const logo = band?.image_url || band?.logo || band?.image || null;
      // Resolve a server-path logo (/static/uploads/...webp) to a data URL so
      // the server-side pdfmake can embed it. A raw path 500s ("File not found
      // in virtual file system") because Node pdfmake can't read it. WebP/GIF/
      // AVIF are re-encoded to PNG. (2026-08-11)
      let resolved: string | null = logo;
      if (logo && !logo.startsWith('data:')) {
        resolved = (await resolveImageToDataUrl(logo)) ?? null;
      } else if (logo && logo.startsWith('data:') && !logo.startsWith('data:image/png') && !logo.startsWith('data:image/jpeg')) {
        resolved = (await reencodeDataUrlToPng(logo)) ?? null;
      }
      setBandLogoUrl(resolved);
    }).catch(() => { if (!cancelled) setBandLogoUrl(null); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Load the Team registry for this band so the PDF hospitality section can
  // resolve each person's diet from their Team identity (the rider cards no
  // longer carry `diet`). Keyed by the SHARED team_member id.
  useEffect(() => {
    if (!activeProfileId) { setDietById({}); return; }
    let cancelled = false;
    setDietById({});
    void apiGetTeam({ band: activeProfileId }).then((members) => {
      if (cancelled) return;
      const map: Record<string, { diet?: string; dietNotes?: string; nonFoodAllergies?: string }> = {};
      for (const m of members) {
        map[m.id] = { diet: m.diet || '', dietNotes: m.diet_notes || '', nonFoodAllergies: m.non_food_allergies || '' };
      }
      setDietById(map);
    }).catch(() => { if (!cancelled) setDietById({}); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Promoter sharing moved to the show's rider (ShowDetailPage Tech tab) —
  // the show is the central entity since 2026-08-07.

  const handlePdfDownload = async () => {
    const selectedPlot = stagePlots.find((plot) => plot.id === selectedStagePlotId)?.data ?? null;
    let stageImage: string | null = null;
    if (selectedPlot) {
      stageImage = await stagePlotToPdfImage(selectedPlot);
    }
    // Backline images may be server URLs (/static/uploads/...) — pdfmake runs
    // server-side and cannot fetch them, so resolve to data URLs first.
    const [musiciansResolved, crewResolved] = await Promise.all([
      resolveBacklineImages<Musician>(musicians),
      resolveBacklineImages<CrewMember>(crew),
    ]);
    const profile = getProfile(activeProfileId ?? '');
    const docDef = buildPdfDoc(
      profile?.name,
      { transport: riderCfg.transport, activeMusicianIds: riderCfg.activeMusicianIds, activeCrewIds: riderCfg.activeCrewIds, requirements: null },
      config, musiciansResolved, crewResolved, selectedPlot, stageImage, bandLogoUrl, dietById,
      undefined,
      teamManager,
      undefined,
      pdfLayout,
    );
    try {
      const blobUrl = await generatePdfBlobViaServer(docDef);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Ask the file NAME first (user 23.10) — pre-filled, Cancel aborts.
      const chosen = window.prompt(t('showDetail.pdfNameTitle'), 'technical-rider.pdf');
      if (chosen === null) { setTimeout(() => URL.revokeObjectURL(blobUrl), 1000); return; }
      a.download = chosen.trim() || 'technical-rider.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      // revoke after the download has been triggered
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'PDF download failed');
      reportSaveError('rider', err);
    }
  };

  const handleStagePlotSelect = async (val: string) => {
    if (val === '__new__') {
      if (!activeProfileId) return;
      // POST /api/stageplots — delegated to `content.rider` (rider content).
      if (!canWriteStagePlots) return;
      try {
        const created = await createStagePlot({
          name: 'New Stage Plot',
          bandId: activeProfileId,
          data: createEmptyStagePlot(),
        });
        const result = await listStagePlots({ bandId: activeProfileId });
        setStagePlots(result.items);
        // Open the Stage Plot Editor directly on the newly created plot.
        navigate(`${base}/stageplot-editor?edit=${created.id}`);
        reportSaveOk('stageplots');
      } catch (err) {
        reportSaveError('stageplots', err);
      }
      return;
    }
    setSelectedStagePlotId(val || null);
  };

  return (
    <div className="page rider-page">
      <PageHeader
        title={ui('riderPage.title')}
        actions={
          <>
            {/* L2 — « Import AI » est une ACTION D'ÉCRITURE : elle n'existe pas pour un
                profil en lecture pure (l'écran lit, il n'édite pas). Download PDF reste :
                c'est la raison d'être du preview. */}
            {!pureReadonly && (
              <button
                className="btn btn-secondary"
                onClick={() => setImportOpen(true)}
                disabled={!canWriteNonDelegated}
              >
                {ui('riderPage.importAi')}
              </button>
            )}
            <button className="btn btn-primary" onClick={handlePdfDownload}>
              {ui('riderPage.downloadPdf')}
            </button>
          </>
        }
      />

      {readOnlyNote && !pureReadonly && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{ui('common.readOnlyAccount')}</p>
      )}

      {pureReadonly ? (
        // LECTURE PURE (viewer) : transport en texte, line-up en liste, stage plot en
        // image — AUCUN contrôle `disabled` et aucune note « read-only account ».
        <RiderReadOnlyView
          transport={riderCfg.transport}
          musicians={musicians}
          crew={crew}
          activeMusicianIds={riderCfg.activeMusicianIds || []}
          activeCrewIds={riderCfg.activeCrewIds || []}
          stagePlots={stagePlots}
        />
      ) : (
      <div className="rider-card">
        <div className="rider-card-section">
          <h2>{ui('riderPage.transport')}</h2>
          <TransportToggle
            value={riderCfg.transport}
            onChange={(t) => updateRider({ transport: t })}
            disabled={!canEditContent}
          />
        </div>

        <div className="rider-card-section">
          <h2>{ui('riderPage.activeMusicians')}</h2>
          <div className="checkbox-group-list musician-inline-list">
            {sortedMusicians.map((m) => (
              <label key={m.id} className="musician-checkbox">
                <input
                  type="checkbox"
                  checked={(riderCfg.activeMusicianIds || []).includes(m.id)}
                  onChange={() => toggleMusician(m.id)}
                  disabled={!canEditContent}
                />
                <span className="musician-dot" style={{ backgroundColor: m.color }} />
                {m.name || ui('musicians.unnamed')}
                {m.fonction && <span className="musician-role-tag">{m.fonction}</span>}
              </label>
            ))}
            {sortedMusicians.length === 0 && (
              <p className="empty-hint">
                {ui('riderPage.noMusicians')}
              </p>
            )}
          </div>
        </div>

        <div className="rider-card-section">
          <h2>{ui('riderPage.activeCrew')}</h2>
          <div className="checkbox-group-list musician-inline-list">
            {sortedCrew.map((c) => (
              <label key={c.id} className="musician-checkbox">
                <input
                  type="checkbox"
                  checked={riderCfg.activeCrewIds?.includes(c.id) ?? false}
                  disabled={!canEditContent}
                  onChange={() => {
                    if (!canEditContent) return; // PUT /rider/config 403s without the capability
                    const ids = riderCfg.activeCrewIds || [];
                    updateRider({
                      activeCrewIds: ids.includes(c.id)
                        ? ids.filter((i) => i !== c.id)
                        : [...ids, c.id],
                    });
                  }}
                />
                <span className="crew-dot">👤</span>
                {c.name || c.role || ui('crew.unnamed')}
              </label>
            ))}
            {crew.length === 0 && (
              <p className="empty-hint">
                {ui('riderPage.noCrew')}
              </p>
            )}
          </div>
        </div>

        <div className="rider-card-section">
          <h2>{ui('riderPage.stagePlot')}</h2>
          <label className="form-group">
            <span>{ui('riderPage.canonicalPlot')}</span>
          </label>
          <div className="stageplot-thumbnails">
            {stagePlots.map((plot) => (
              <div
                key={plot.id}
                className="stageplot-thumb-card"
                role="button"
                tabIndex={0}
                onClick={() => handleStagePlotSelect(plot.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStagePlotSelect(plot.id);
                  }
                }}
              >
                <StagePlotThumb plot={plot.data} selected={plot.id === selectedStagePlotId} />
                <span className="stageplot-thumb-name">{plot.name}</span>
              </div>
            ))}
            <button
              type="button"
              className="stageplot-add-card"
              onClick={() => handleStagePlotSelect('__new__')}
              disabled={!canWriteStagePlots}
            >
              <span className="stageplot-add-icon" aria-hidden="true">+</span>
              {ui('riderPage.createNewStagePlot')}
            </button>
          </div>
        </div>

        <div className="rider-card-section">
          <h2>{ui('riderPage.sharePromoter')}</h2>
          <p style={{ color: 'var(--text-dim)', margin: 0, fontSize: '0.9rem' }}>
            <Trans i18nKey="riderPage.sharingNote" components={{ strong: <strong /> }}>
              Sharing now lives on the <strong>show</strong>: open a show and use
              “Share rider (promoter)” in its Tech tab. The promoter validates that
              show’s rider — transport, people and requirements may differ per show.
            </Trans>
          </p>
        </div>
      </div>
      )}

      <div className="rider-preview-container">
        <h2>{ui('riderPage.pdfPreview')}</h2>
        {pdfError ? (
          <div className="rider-preview-placeholder rider-preview-error">{ui('riderPage.pdfError')} {pdfError}</div>
        ) : pdfDoc ? (
          <PdfPreview docDef={pdfDoc} />
        ) : (
          <div className="rider-preview-placeholder">
            {(riderCfg.activeMusicianIds || []).length === 0
              ? ui('riderPage.selectMusicians')
              : ui('riderPage.generatingPreview')}
          </div>
        )}
      </div>

      {importOpen && !pureReadonly && (
              <ImportRiderAIModal
                bandId={activeProfileId}
                bandName={activeProfileId ? getProfile(activeProfileId)?.name : undefined}
                onClose={() => setImportOpen(false)}
                onApplied={() => {
                  void reload();
                }}
                onStagePlotCreated={(created) => {
                  setImportOpen(false);
                  void reload();
                  // Refresh the plot list so the new plot shows up immediately.
                  if (activeProfileId) {
                    void listStagePlots({ bandId: activeProfileId }).then((r) => setStagePlots(r.items));
                  }
                  // Open the editor directly on the imported plot.
                                    navigate(`${base}/stageplot-editor?edit=${created.id}`);
                                  }}
              />
            )}
    </div>
  );
}
